/*
The copy of the XOL-586 request e-mail surfaces: Emails Update (`emailSetup.*`),
the result of a server-side send (`requestMail.notify.*` / `.test.*` / `.skipped.*`), the per-party
counts of an e-mail (`requestMail.parties.*`),
the automatic upload and file-approved notices (`requestMail.notice.*`) and the mail
ledger views: the step 2 / 5 last-sent line and the batch dialog (`requestMail.step.*`,
`requestMail.result.*`, `requestMail.tag.*`, `requestMail.batch.*`, `requestMail.detail.*`),
and the request Activity log (`requestActivity.*`): the list's filters and states, one
sentence per activity kind, and the before / after blocks of the field-change dialog.
Account e-mail (XOL-602/603/604: password links and "password changed" notices) has the same
three surfaces under `accountMail.*`: the send action, the history and the stored copy.
Invite by e-mail to register (same ticket) is `contactInvite.*`: the toolbar button, the dialog, the
Invitations panel and the stored copy of an invitation e-mail (`contactInvite.preview.*`).
XOL-67 adds the RTA (Request to Acquire) arms to the same tables: the activity sentence of each RTA kind,
the labels and trigger lines of `rta_notice` / `rta_decision` mail, the decision e-mail's toast, the
counts-only `notice` a non-Tower-Co caller gets, and the RTA variants of Emails Update's help and off copy.

The UX spec (docs/plans/2026-10-16-xol-586-server-side-request-mail-and-activity-log.md,
"## 10. UX spec") gives every string a stable dotted key. This module is the one place
they live, in the style of requestSyncCopy.js: plain English (the app has no i18n
framework, XOL-158), template functions that take ONE params object, ES5, no optional
chaining or nullish coalescing.

The Activity log's pure parts (the row -> display model, the query string, the paging
facts, the field-change blocks) sit at the end of this file so a spec can drive them
without mounting anything. Field labels and value text are NOT re-implemented here:
they come from requestSyncCopy.fieldLabel and syncText.

    import { activityText, notifyOutcome, noticeOutcome, mailResult } from '@/lib/requestActivity'

    activityText('emailSetup.users.title', { n: 3 })     // 'Users (3)'
------------------------------------------------------------------------ */

import { fieldLabel, copyText } from '@/lib/requestSyncCopy'
import { valueKind, describeScalar, describeEntry } from '@/lib/syncText'
import { recipientKey } from '@/lib/requestRecipients'
import { appNow, toAppTime } from '@/lib/appTime'
import { stepName as rtaStepName } from '@/lib/rtaStage'

function plural (n, one, many) {
  return Number(n) === 1 ? one : many
}

function has (obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

// An organisation label for the "not e-mailed" lines.
function orgOf (p) {
  return p.org || 'the organisation'
}

var COPY = {

  // -- Emails Update: containers, states --------------------------------
  'emailSetup.intro.title': 'Who gets e-mails',
  'emailSetup.intro.body': function (p) {
    return 'Users get request e-mails when the matching alert is on in their User Profile. For this ' + (p.rtx || 'request') + ' only, add people under Additional, or use the bell beside a user to turn their e-mails off or back on.'
  },
  'emailSetup.refresh': 'Refresh',
  'emailSetup.off.title': function (p) { return 'E-mails are off for this ' + p.rtx },
  'emailSetup.off.body': 'Nobody gets upload or approval e-mails until Send E-mails is turned on. Test e-mails still work.',
  'emailSetup.off.body.rta': 'Nobody gets RTA e-mails until Send E-mails is turned on. Test e-mails still work.',
  'emailSetup.loading': 'Loading recipients…',
  'emailSetup.error.title': 'Couldn\'t load the recipient lists',
  'emailSetup.error.body': 'Check your connection, then try again.',
  'emailSetup.error.retry': 'Try again',
  'emailSetup.error.forbidden': function (p) { return 'Only ' + p.towerCo + ' users can see this request\'s recipients.' },
  'emailSetup.error.notFound': 'This request wasn\'t found. Close it and open it again.',

  // -- Column header and help ------------------------------------------
  'emailSetup.party.towerco': 'Tower Co',
  'emailSetup.party.owner': 'Telco (owner)',
  'emailSetup.party.provider': 'Provider',
  'emailSetup.help.towerco': function (p) { return 'Gets upload notices only. Approval e-mails go to ' + (p.owner || 'the telco') + '.' },
  'emailSetup.help.owner': 'Gets upload notices and approval e-mails (step 2 and step 5 decisions).',
  'emailSetup.help.provider': 'Gets upload notices only.',
  // XOL-67 (UX v4 section 2): an RTA's two columns are Approval only, and the RTA's client is not its "owner".
  'emailSetup.party.owner.rta': 'Telco (client)',
  'emailSetup.help.towerco.rta': function (p) {
    return 'Gets RTA e-mails from ' + (p.owner || 'the telco') + ': a short note when it requests a site or proposes options, and the reviews it submits. Uses Approval Alerts.'
  },
  'emailSetup.help.owner.rta': function (p) {
    return 'Gets RTA e-mails from ' + (p.towerCo || 'the Tower Co') + ': a short note when it opens an RTA, the options it proposes in detail, its reviews and Step 4 declines, the decision e-mail, and the completion or rejection notice. Uses Approval Alerts.'
  },
  // Only if the file-approved trigger is turned on again (the API then sends the provider an approval alert).
  'emailSetup.help.provider.fileApproved': 'Gets upload notices and file-approved notices.',

  // -- Summary ----------------------------------------------------------
  'emailSetup.summary.upload': function (p) { return p.n + ' ' + p.will + ' get upload notices' },
  'emailSetup.summary.upload.none': function (p) { return 'Nobody ' + p.will + ' get upload notices' },
  'emailSetup.summary.approval': function (p) { return p.n + ' ' + p.will + ' get approval e-mails' },
  'emailSetup.summary.approval.none': function (p) { return 'Nobody ' + p.will + ' get approval e-mails' },
  'emailSetup.summary.fileApproved': function (p) { return p.n + ' ' + p.will + ' get file-approved notices' },
  'emailSetup.summary.fileApproved.none': function (p) { return 'Nobody ' + p.will + ' get file-approved notices' },
  'emailSetup.summary.problems': function (p) {
    return p.p + ' with ' + plural(p.p, 'an e-mail problem', 'e-mail problems')
  },
  'emailSetup.nobody.title': function (p) {
    return 'Nobody at ' + p.org + ' ' + p.will + ' get e-mails for this ' + p.rtx
  },
  'emailSetup.nobody.body': function (p) {
    return 'Add a user or an address under Additional, or ask ' + p.org + ' users to turn on Upload Alerts or Approval Alerts in their User Profile.'
  },
  // XOL-67 (B10): an RTA's recipients follow Approval Alerts only (decision 58).
  'emailSetup.nobody.body.rta': function (p) {
    return 'Add a user or an address under Additional, or ask ' + p.org + ' users to turn on Approval Alerts in their User Profile.'
  },
  // XOL-67 (B10, UX v4 section 2): the info alert where the off alert sits.
  'emailSetup.tcClient.title': 'No e-mails are sent for this RTA',
  'emailSetup.tcClient.body': function (p) { return (p.towerCo || 'The Tower Co') + ' is its own client, so RTA e-mails are switched off.' },
  'emailSetup.terminal.title': 'This RTA is closed, so no more e-mails are sent.',

  // -- Users ------------------------------------------------------------
  'emailSetup.users.title': function (p) { return 'Users (' + p.n + ')' },
  'emailSetup.users.aria': function (p) { return p.org + ' users' },
  'emailSetup.users.empty': function (p) { return p.org + ' has no active users.' },
  'emailSetup.users.receiving': function (p) { return 'E-mail recipients (' + p.n + ')' },
  'emailSetup.users.receivingMatch': function (p) { return 'E-mail recipients (' + p.m + ' of ' + p.n + ')' },
  'emailSetup.users.showAll': function (p) { return 'Show all ' + p.n },
  'emailSetup.users.showFewer': 'Show fewer',
  'emailSetup.users.showNoAddress': function (p) {
    return 'Show ' + p.n + ' ' + plural(p.n, 'user', 'users') + ' with alerts on but no e-mail address'
  },
  'emailSetup.users.hideNoAddress': 'Hide users with no e-mail address',
  'emailSetup.users.noAddressMatch': function (p) { return 'Alerts on, no e-mail address (' + p.m + ')' },
  'emailSetup.users.allHidden': function (p) { return 'None of the ' + p.org + ' users has an e-mail address.' },
  'emailSetup.users.noneReceive': function (p) {
    return 'None of the ' + p.org + ' users gets e-mails for this ' + p.rtx + '. Add people under Additional.'
  },
  'emailSetup.users.search.placeholder': 'Search users',
  'emailSetup.users.search.aria': function (p) { return 'Search ' + p.org + ' users by name or e-mail' },
  'emailSetup.users.search.none': function (p) { return 'No ' + p.org + ' users match “' + p.q + '”.' },
  'emailSetup.user.noEmail': 'No e-mail address',
  'emailSetup.user.unsubscribe.title': function (p) { return 'Unsubscribe for this ' + p.rtx },
  'emailSetup.user.unsubscribe.aria': function (p) { return 'Unsubscribe ' + p.name + ' from ' + p.kinds },
  // A column with two alerts: the bell opens a panel that can turn alerts off or on, so it is named for the panel, not the action.
  'emailSetup.user.panel.aria': function (p) { return 'E-mails for ' + p.name + ' on this ' + p.rtx },
  // The plain bell: an alert is Off and the user has an Additional entry or an unsubscribe (Delta 5 / 5b).
  'emailSetup.user.resubscribe.title': function (p) { return 'Turn e-mails back on for this ' + p.rtx },
  'emailSetup.user.resubscribe.aria': function (p) { return 'Turn e-mails back on for ' + p.name },

  // -- The alert cells of a user row: `{alert}` is Upload / Approval ------
  'emailSetup.cell.on': 'On',
  'emailSetup.cell.added': 'Added',
  'emailSetup.cell.off': 'Off',
  'emailSetup.cell.on.title': 'On in their User Profile',
  'emailSetup.cell.added.title': 'Added for this request under Additional',
  'emailSetup.cell.unsubscribed.title': function (p) { return 'Unsubscribed for this ' + p.rtx },
  'emailSetup.cell.off.title': 'Off in their User Profile',
  'emailSetup.cell.legend': function (p) {
    return 'A plain bell means an alert is Off for this user and can be turned on here, for this ' + p.rtx + ' only. “Added” is also for this ' + p.rtx + ' only.'
  },

  // -- Invalid addresses ------------------------------------------------
  'emailSetup.invalid.short.bad_format': 'Invalid e-mail',
  'emailSetup.invalid.short.last_failed': function (p) { return 'Last e-mail failed' + (p.date ? ' on ' + p.date : '') },
  'emailSetup.invalid.short.no_email': 'No e-mail address',
  'emailSetup.invalid.fix': 'How to fix',
  'emailSetup.invalid.fixHide': 'Hide',
  'emailSetup.invalid.fix.aria': function (p) { return 'How to fix the e-mail address of ' + p.who },
  'emailSetup.invalid.tag.no_email': 'No e-mail',
  'emailSetup.invalid.tag.bad_format': 'Invalid e-mail',
  'emailSetup.invalid.tag.last_failed': 'Last e-mail failed',
  'emailSetup.invalid.no_email': 'There\'s no e-mail address on this user\'s profile, so they get no e-mails.',
  'emailSetup.invalid.bad_format': function (p) {
    return '“' + p.email + '” isn\'t a valid e-mail address, so e-mails to it fail.'
  },
  'emailSetup.invalid.last_failed': function (p) {
    return 'The last request e-mail to this address failed' + (p.date ? ' on ' + p.date : '') + (p.reason ? ' — ' + p.reason : '') + '.'
  },
  'emailSetup.invalid.reason.invalid_address': 'not a valid address',
  'emailSetup.invalid.reason.transport': 'the mail server refused it',
  'emailSetup.invalid.hint.profile': 'Fix it in their user profile.',
  'emailSetup.invalid.hint.typed': 'Remove it and add the correct address.',
  'emailSetup.invalid.link.org': function (p) { return 'Open ' + p.org + ' contacts in Settings' },
  'emailSetup.invalid.link.self': 'Open your User Profile',

  // -- Unsubscribe panel ------------------------------------------------
  'emailSetup.unsub.prompt': function (p) { return 'E-mails for ' + p.name + ' on this ' + p.rtx },
  'emailSetup.unsub.upload': 'Upload notices',
  'emailSetup.unsub.approval': 'Approval e-mails',
  'emailSetup.unsub.confirm': 'Save',
  'emailSetup.unsub.cancel': 'Cancel',

  // -- Additional -------------------------------------------------------
  'emailSetup.additional.title': function (p) { return 'Additional (' + p.n + ')' },
  'emailSetup.overrides.title': function (p) { return 'Changes for this ' + p.rtx + ' only' },
  'emailSetup.additional.help': function (p) { return 'Get e-mails for this ' + p.rtx + ' even if their alert is off.' },
  'emailSetup.additional.empty': 'No one added.',
  'emailSetup.entry.typed': 'Typed address',
  'emailSetup.entry.missing': 'Deleted user',
  'emailSetup.entry.missing.tag': 'Deleted user',
  'emailSetup.entry.missing.hint': 'This user no longer exists. Remove this entry.',
  'emailSetup.entry.disabled.tag': 'Disabled user',
  'emailSetup.entry.disabled.hint': 'This user\'s account is disabled, so they get no e-mails.',
  'emailSetup.entry.remove.aria': function (p) { return 'Remove ' + p.who + ' from Additional' },
  'emailSetup.ticks.upload': 'Upload',
  'emailSetup.ticks.approval': 'Approval',
  'emailSetup.ticks.aria': function (p) { return 'E-mails for ' + p.who },
  'emailSetup.ticks.lastTitle': 'At least one must stay ticked. To remove this address, use Remove.',
  'emailSetup.add.user.placeholder': function (p) { return 'Add a ' + p.org + ' user…' },
  'emailSetup.add.user.aria': function (p) { return 'Add a ' + p.org + ' user under Additional' },
  'emailSetup.add.user.noData': function (p) { return p.org + ' has no active users.' },
  'emailSetup.add.user.noMatch': 'No matching users',
  'emailSetup.add.user.already': 'Already added',
  'emailSetup.add.user.noValid': 'No valid e-mail',
  'emailSetup.add.user.getsAll': 'Already gets every e-mail',
  'emailSetup.add.email.placeholder': 'Or type an e-mail address',
  'emailSetup.add.email.aria': function (p) { return 'E-mail address to add for ' + p.org },
  'emailSetup.add.email.button': 'Add',
  'emailSetup.add.error.format': 'Enter a valid e-mail address, like name@example.com.',
  'emailSetup.add.error.duplicate': function (p) { return p.email + ' is already under Additional.' },
  'emailSetup.add.error.isUser': function (p) {
    return 'That address belongs to ' + p.name + '. Choose them from the user list above instead, so changes to their profile are followed.'
  },

  // -- Unsubscribed -----------------------------------------------------
  'emailSetup.unsubscribed.title': function (p) { return 'Unsubscribed (' + p.n + ')' },
  // Delta 5: only entries with no row above (a deleted or disabled contact, a user with no address).
  'emailSetup.unsubscribed.help': function (p) { return 'Not listed under Users, but still switched off for this ' + p.rtx + '. Re-subscribe removes the entry.' },
  'emailSetup.unsubscribed.resubscribe': 'Re-subscribe',
  'emailSetup.unsubscribed.resubscribe.aria': function (p) { return 'Re-subscribe ' + p.who },

  // -- Send Test --------------------------------------------------------
  'emailSetup.test.button': 'Send Test Email',
  'emailSetup.test.helper': function (p) {
    return 'Sends a test to the ' + p.n + ' ' + plural(p.n, 'person', 'people') + ' this column would e-mail.'
  },
  'emailSetup.test.whileOff': 'Test e-mails are sent even while Send E-mails is off.',
  'emailSetup.test.disabledTitle': 'Nobody in this column can be e-mailed, so there\'s nothing to test.',

  // -- Result of a server-side send (POST /actions/{id}/notify) ----------
  'requestMail.party.towerco': 'Tower Co',
  'requestMail.party.owner': 'Telco',
  'requestMail.party.provider': 'Provider',
  // Delta 3: how many addresses each party got, "TLB (Telco) 8 · BHG (Provider) 0".
  'requestMail.parties.label': function (p) { return p.org ? p.org + ' (' + p.party + ')' : p.party },
  'requestMail.parties.item': function (p) { return p.label + ' ' + p.n },
  'requestMail.alerts.upload': 'Upload Alerts',
  'requestMail.alerts.approval': 'Approval Alerts',
  'requestMail.notify.label.approval': function (p) { return p.status + ' e-mail' },
  'requestMail.notify.label.test': 'Test e-mail',
  // XOL-67: the Tower Co decision e-mail of an RTA (UX v4 section 3): "Approved decision e-mail is being sent to...".
  'requestMail.notify.label.rtaDecision': function (p) { return p.status + ' decision e-mail' },
  'requestMail.notify.label.rtaDecision.bare': 'Decision e-mail',
  'requestMail.notify.title.notSent': 'E-mail not sent',
  'requestMail.notify.queued': function (p) {
    return p.label + ' is being sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + (p.parties ? ' — ' + p.parties : '') + '. Delivery status is in Activity.'
  },
  'requestMail.notify.requeued': function (p) {
    return p.label + ' is being re-sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + (p.parties ? ' — ' + p.parties : '') + '. Delivery status is in Activity.'
  },
  'requestMail.notify.requeuedSubset': function (p) {
    return p.label + ' is being re-sent to ' + p.n + ' selected ' + plural(p.n, 'recipient', 'recipients') + (p.parties ? ' — ' + p.parties : '') + '. Delivery status is in Activity.'
  },
  'requestMail.notify.partial.title': function (p) { return p.label + ': not everyone was e-mailed' },
  'requestMail.notify.partial.queued': function (p) {
    return 'Being sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + '.'
  },
  'requestMail.notify.partial.failed': function (p) {
    return p.f + ' ' + plural(p.f, 'address couldn\'t', 'addresses couldn\'t') + ' be used: ' + p.emails + '.'
  },
  'requestMail.notify.partial.footer': 'Fix addresses in Emails Update or the user\'s profile. Details are in Activity.',
  // A counts-only notice (a non-Tower-Co caller, Q9) says how many addresses failed, never which.
  'requestMail.notify.partial.failedCount': function (p) {
    return p.f + ' ' + plural(p.f, 'address couldn\'t', 'addresses couldn\'t') + ' be used.'
  },
  'requestMail.notify.duplicate': 'That e-mail was already sent. No second copy was sent.',
  'requestMail.notify.invalidState': function (p) {
    return 'Choose a decision for Step ' + p.n + ' first, then send its e-mail.'
  },
  // v2 (API-10): the Step 3 decision is Approve only, so only a Pending decision is refused (invalid_state); the Rejected / reason_required arm is gone.
  'requestMail.notify.invalidState.rta': 'Set the decision to Approved first, then e-mail it.',
  'requestMail.notify.requestClosed': function (p) {
    return 'This ' + p.rtx + ' is already closed, so an approval e-mail can\'t be sent.'
  },
  'requestMail.notify.noRecipients': function (p) {
    return 'Nobody could be e-mailed: ' + p.skippedList + '. Add someone under Additional in Emails Update, or ask them to turn on ' + p.alerts + ' in their User Profile.'
  },
  'requestMail.notify.nothingQueued': function (p) {
    return 'Nobody could be e-mailed: ' + p.f + ' ' + plural(p.f, 'address couldn\'t', 'addresses couldn\'t') + ' be used (' + p.emails + '). Fix them in Emails Update or the user\'s profile.'
  },
  'requestMail.notify.forbidden': function (p) { return 'Only ' + p.towerCo + ' users can send e-mails for a request.' },
  'requestMail.notify.tooMany': 'Too many e-mails sent in a short time. Wait a minute, then try again.',
  'requestMail.notify.fallback': function (p) { return 'The e-mail wasn\'t sent.' + (p.message ? ' ' + p.message : '') },
  'requestMail.test.queued': function (p) {
    return 'Test e-mail is being sent to ' + p.n + ' ' + p.org + ' ' + plural(p.n, 'recipient', 'recipients') + '. Delivery status is in Activity.'
  },
  'requestMail.test.noRecipients': function (p) {
    return 'Nobody in the ' + p.org + ' column can be e-mailed, so there\'s nothing to test.'
  },

  // -- Mail labels, results and tags (one e-mail event, one line) --------
  'requestMail.label.approval_notice': function (p) { return 'Approval e-mail (' + p.status + ')' },
  'requestMail.label.approval_notice.bare': 'Approval e-mail',
  'requestMail.label.upload_notice': function (p) { return 'Upload notice (' + p.file + ')' },
  'requestMail.label.upload_notice.bare': 'Upload notice',
  'requestMail.label.file_approved': function (p) { return 'File approved notice (' + p.file + ')' },
  'requestMail.label.file_approved.bare': 'File approved notice',
  'requestMail.label.test': function (p) { return 'Test e-mail (' + p.org + ')' },
  'requestMail.label.test.bare': 'Test e-mail',
  // XOL-67 (UX v4 section 3). rta_decision is the Tower Co decision e-mail; rta_notice is every
  // other RTA e-mail, labelled by the activity kind that triggered it (`event`).
  'requestMail.label.rta_decision': function (p) { return p.status + ' decision e-mail' },
  'requestMail.label.rta_decision.bare': 'Decision e-mail',
  'requestMail.label.rta_notice.bare': 'RTA notice',
  // v2 (UX spec 4.8, API-16): the proposal goes to the client in detail and to the Tower Co as a note; the other events name their option.
  // v2.1 (decision 158 Q1, API P-S30): creating an RTA e-mails the other side a short note (event `request_created`).
  'requestMail.label.rta_notice.request_created': 'Request note',
  'requestMail.label.rta_notice.options_proposed': 'Proposal e-mail',
  'requestMail.label.rta_notice.options_proposed.detailed': 'Proposal e-mail (detailed)',
  'requestMail.label.rta_notice.options_proposed.note': 'Proposal note',
  'requestMail.label.rta_notice.option_reviewed': function (p) { return 'Review notice (option ' + p.n + ')' },
  'requestMail.label.rta_notice.option_reviewed.bare': 'Review notice',
  'requestMail.label.rta_notice.option_declined': function (p) { return 'Decline notice (option ' + p.n + ')' },
  'requestMail.label.rta_notice.option_declined.bare': 'Decline notice',
  'requestMail.label.rta_notice.request_completed': 'Completion notice',
  'requestMail.label.rta_notice.request_rejected': 'Rejection notice',
  'requestMail.label.other': 'E-mail',
  'requestMail.result.sent': function (p) { return 'sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.sentSome': function (p) { return 'sent to ' + p.s + ' of ' + p.n + ' recipients' },
  'requestMail.result.resent': function (p) { return 're-sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.resentSome': function (p) { return 're-sent to ' + p.s + ' of ' + p.n + ' recipients' },
  'requestMail.result.sending': function (p) { return 'sending to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.sentSelected': function (p) { return 'sent to ' + p.n + ' selected ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.resentSelected': function (p) { return 're-sent to ' + p.n + ' selected ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.sentSelectedSome': function (p) { return 'sent to ' + p.s + ' of ' + p.n + ' selected recipients' },
  'requestMail.result.resentSelectedSome': function (p) { return 're-sent to ' + p.s + ' of ' + p.n + ' selected recipients' },
  'requestMail.result.sendingSelected': function (p) { return 'sending to ' + p.n + ' selected ' + plural(p.n, 'recipient', 'recipients') },
  'requestMail.result.sentSoFar': function (p) { return p.s + ' sent so far' },
  'requestMail.result.failed': function (p) { return p.f + ' failed' },
  'requestMail.result.allFailed': function (p) { return 'not sent — all ' + p.n + ' failed' },
  'requestMail.result.oneFailed': 'not sent — failed',
  'requestMail.result.mailOff': function (p) { return 'not sent — e-mails are turned off for this ' + (p.rtx || 'request') },
  'requestMail.result.noRecipients': 'not sent — no one to e-mail',
  'requestMail.result.reason.invalid_address': 'invalid address',
  'requestMail.result.reason.transport': 'mail server error',
  'requestMail.result.reason.not_run': 'send job never started',
  'requestMail.result.reason.job_lost': 'retries stopped before it went out',
  'requestMail.result.reason.job_failed': 'send job failed',
  'requestMail.result.reason.dispatch_failed': 'couldn\'t be queued',
  'requestMail.tag.sent': 'Sent',
  'requestMail.tag.sending': 'Sending',
  'requestMail.tag.partial': 'Partly failed',
  'requestMail.tag.failed': 'Failed',
  'requestMail.tag.notSent': 'Not sent',

  // -- Step 2 / 5: the Send / Re-send button, its last-sent line, the confirms
  'requestMail.step.send': function (p) { return 'Send ' + p.status + ' e-Mail' },
  'requestMail.step.resend': function (p) { return 'Re-send ' + p.status + ' e-Mail' },
  'requestMail.step.mailOffTitle': function (p) {
    return 'E-mails are turned off for this ' + p.rtx + ', so this e-mail can\'t be sent. Turn on Send E-mails in Emails Update to send it.'
  },
  'requestMail.send.sending': 'Sending…',
  'requestMail.step.lastSent': function (p) {
    return p.status + ' e-mail ' + p.result + ' — ' + p.datetime + (p.name ? ' by ' + p.name : '')
  },
  'requestMail.step.lastSent.aria': function (p) { return 'Show who received the ' + p.status + ' e-mail sent ' + p.datetime },
  'requestMail.step.lastSent.details': 'Details',

  // -- The Re-send recipient picker (Delta 1, item 4) --------------------
  'requestMail.resend.title': function (p) { return 'Re-send ' + p.status + ' e-mail' },
  'requestMail.resend.titleFirst': function (p) { return 'Send ' + p.status + ' e-mail' },
  'requestMail.resend.intro': function (p) {
    return 'Choose who gets the ' + p.status + ' e-mail again. Addresses that haven\'t received this version are selected.'
  },
  // The same without "again": nothing has gone out for this version yet (no last_batch).
  'requestMail.resend.introFirst': function (p) {
    return 'Choose who gets the ' + p.status + ' e-mail. Addresses that haven\'t received this version are selected.'
  },
  'requestMail.resend.lastSent': function (p) {
    var t = 'Last sent ' + p.datetime + (p.name ? ' by ' + p.name : '') + (p.subset ? ', to selected recipients' : '')

    // A name that ends in a full stop ("Aisyah R.") must not get a second one.
    return /\.$/.test(t) ? t : t + '.'
  },
  'requestMail.resend.loading': 'Loading recipients…',
  'requestMail.resend.selectAll': 'Select all',
  'requestMail.resend.selectNone': 'Select none',
  'requestMail.resend.count': function (p) { return p.k + ' of ' + p.n + ' selected' },
  'requestMail.resend.group.pending': function (p) { return 'Not received yet (' + p.n + ')' },
  // D31: only a failure of the queue is selected; one the mail server refused is listed, not ticked.
  'requestMail.resend.group.pending.help': 'New or changed addresses, and ones the send queue failed last time. Other failed addresses are listed but not selected.',
  'requestMail.resend.group.done': function (p) { return 'Already received, or still sending (' + p.n + ')' },
  'requestMail.resend.group.done.help': 'Tick any that need another copy.',
  'requestMail.resend.group.invalid': function (p) { return 'Can\'t be e-mailed (' + p.n + ')' },
  'requestMail.resend.group.invalid.help': 'Fix these in Emails Update or the user\'s profile, then re-send.',
  'requestMail.resend.group.aria': function (p) { return 'Select every address in ' + p.group },
  'requestMail.resend.tag.new': 'Not received',
  'requestMail.resend.tag.failed': 'Failed',
  'requestMail.resend.tag.received': 'Received',
  'requestMail.resend.tag.sending': 'Sending',
  'requestMail.resend.tag.invalid': 'Invalid address',
  'requestMail.resend.detail.new': 'Not sent this version yet',
  'requestMail.resend.detail.failed': function (p) { return 'Last attempt failed' + (p.reason ? ' — ' + p.reason : '') },
  // D31: a failure where no attempt ran (the job never started, or it was never queued) is not "a failed attempt".
  'requestMail.resend.detail.notSent': function (p) { return 'Not sent' + (p.reason ? ' — ' + p.reason : '') },
  'requestMail.resend.detail.received': function (p) { return 'Sent ' + p.datetime },
  'requestMail.resend.detail.sending': 'Still being sent — ticking it may send a second copy',
  'requestMail.resend.detail.invalid': 'Not a valid e-mail address',
  'requestMail.resend.send': function (p) { return 'Re-send to ' + p.k + ' ' + plural(p.k, 'recipient', 'recipients') },
  'requestMail.resend.sendFirst': function (p) { return 'Send to ' + p.k + ' ' + plural(p.k, 'recipient', 'recipients') },
  'requestMail.resend.sendNone': 'Re-send',
  'requestMail.resend.sendNoneFirst': 'Send',
  'requestMail.resend.cancel': 'Cancel',
  'requestMail.resend.allReceived.title': 'Everyone has already received this version',
  'requestMail.resend.allReceived.body': 'Nothing is selected. Tick anyone who should get it again.',
  'requestMail.resend.nobody.title': function (p) { return 'Nobody at ' + p.owner + ' gets approval e-mails now' },
  'requestMail.resend.nobody.body': function (p) {
    return 'Add someone under Additional in Emails Update, or ask ' + p.owner + ' users to turn on Approval Alerts in their User Profile.'
  },
  'requestMail.resend.error.title': 'Couldn\'t load the recipient list',
  'requestMail.resend.error.body': 'Nothing was sent. Check your connection, then try again.',
  'requestMail.resend.error.retry': 'Try again',
  'requestMail.resend.error.notFound': 'This request or step wasn\'t found. Close it and open it again.',
  'requestMail.resend.stale.content.title': 'The e-mail changed while this list was open',
  'requestMail.resend.stale.content.body': 'Nothing was sent. The list now shows who has this new version. Check the selection, then send.',
  'requestMail.resend.stale.recipients.title': 'The recipient list changed while it was open',
  'requestMail.resend.stale.recipients.body': function (p) {
    return 'Nothing was sent. ' + p.emails + ' ' + plural(p.n, 'is', 'are') + ' no longer on the list. Check the selection, then send.'
  },
  'requestMail.reject.notSent': function (p) {
    return 'The Rejected e-mail hasn\'t been sent to ' + p.owner + '. Once this ' + p.rtx + ' is closed it can\'t be sent. To send it first, choose Cancel, then Send Rejected e-Mail.'
  },

  'requestMail.complete.notSent': function (p) {
    return 'The ' + p.mailStatus + ' e-mail hasn\'t been sent to ' + p.owner + '. Once this ' + p.rtx + ' is closed it can\'t be sent. To send it first, choose Cancel, then Send ' + p.mailStatus + ' e-Mail.'
  },

  // -- The batch dialog (read-only; GET /actions/{id}/mail/{batch}) ------
  'requestMail.batch.meta.step': 'Step',
  'requestMail.batch.meta.trigger': 'Triggered',
  'requestMail.batch.meta.subject': 'Subject',
  'requestMail.batch.meta.organisation': 'Organisation',
  'requestMail.batch.meta.recipients': 'Recipients',
  'requestMail.batch.subset': function (p) { return 'Selected ' + p.m + ' of ' + p.n + ' current recipients' },
  'requestMail.batch.subsetUnknown': 'Selected recipients only',
  'requestMail.batch.meta.triggerValue': function (p) { return p.trigger + (p.datetime ? ' · ' + p.datetime : '') },
  'requestMail.batch.meta.stepValue': function (p) { return 'Step ' + p.n + (p.title ? ': ' + p.title : '') },
  'requestMail.batch.trigger.auto.upload_notice': function (p) { return 'Automatically, when ' + p.name + ' uploaded “' + p.file + '”' },
  'requestMail.batch.trigger.auto.file_approved': function (p) { return 'Automatically, when ' + p.name + ' approved “' + p.file + '”' },
  // Delta 4: the file is already in the dialog header, so the trigger line leaves it out.
  'requestMail.batch.trigger.auto.upload_notice.short': function (p) { return 'Automatically, after ' + p.name + ' uploaded the file' },
  'requestMail.batch.trigger.auto.file_approved.short': function (p) { return 'Automatically, after ' + p.name + ' approved the file' },
  'requestMail.batch.trigger.auto.system': 'Automatically',
  // XOL-67 (UX v4 section 3, v2 UX spec 4.8): an RTA's automatic e-mails, by the activity kind that triggered them. The three v2
  // events are worded without a name, as the spec words them; the completion and rejection lines are unchanged.
  'requestMail.batch.trigger.auto.rta_notice.request_created': 'Sent when the RTA was created',
  'requestMail.batch.trigger.auto.rta_notice.options_proposed': 'Sent when the options were proposed',
  'requestMail.batch.trigger.auto.rta_notice.option_declined': 'Sent when the option was declined',
  'requestMail.batch.trigger.auto.rta_notice.request_completed': function (p) { return 'Automatically, when ' + p.name + ' completed the RTA' },
  'requestMail.batch.trigger.auto.rta_notice.request_rejected': function (p) { return 'Automatically, when ' + p.name + ' rejected the RTA' },
  // A review submitted with Submit review is a button, not an automatic mail.
  'requestMail.batch.trigger.button.rta_notice.option_reviewed': 'Sent when the review was submitted',
  'requestMail.batch.trigger.button': function (p) { return 'Sent by ' + p.name },
  'requestMail.batch.trigger.repeat': function (p) { return 'Re-sent by ' + p.name },
  'requestMail.batch.trigger.test': function (p) { return 'Test sent by ' + p.name },
  'requestMail.batch.trigger.legacy': function (p) { return 'Sent by ' + p.name + ' from an older version of the app' },
  // Delta 4: the four tiles above the recipients, the party breakdown and the empty list.
  'requestMail.batch.stat.recipients': 'Recipients',
  'requestMail.batch.stat.sent': 'Sent',
  'requestMail.batch.stat.queued': 'Queued',
  'requestMail.batch.stat.failed': 'Failed',
  'requestMail.batch.parties.label': 'By party',
  'requestMail.batch.party.aria': function (p) { return (p.org ? p.org + ' (' + p.party + ')' : p.party) + ': ' + p.n + ' e-mailed' },
  'requestMail.batch.none': 'No one was e-mailed.',
  'requestMail.batch.helper': '“Sent” means the mail server accepted the e-mail. It doesn\'t confirm that it reached the recipient\'s inbox.',
  'requestMail.batch.queued': 'Some e-mails are still queued.',
  'requestMail.batch.refresh': 'Refresh',
  'requestMail.batch.loading': 'Loading recipients…',
  'requestMail.batch.error': 'Couldn\'t load this e-mail\'s recipients.',
  'requestMail.batch.notFound': 'This e-mail record wasn\'t found. Refresh the activity log and try again.',
  'requestMail.batch.forbidden': function (p) { return 'Only ' + p.towerCo + ' users can see who was e-mailed.' },
  'requestMail.batch.retry': 'Try again',
  'requestMail.skipped.title': 'Not e-mailed',
  'requestMail.col.recipient': 'Recipient',
  'requestMail.col.status': 'Status',
  'requestMail.status.queued': 'Queued',
  'requestMail.status.sent': 'Sent',
  'requestMail.status.failed': 'Failed',
  'requestMail.detail.sent': function (p) { return 'Sent ' + p.datetime },
  'requestMail.detail.queued': function (p) { return 'Queued ' + p.datetime },
  'requestMail.detail.attempts': function (p) { return 'tried ' + p.a + ' times' },
  'requestMail.detail.failed': function (p) { return p.reason + (p.datetime ? ' · ' + p.datetime : '') },
  'requestMail.detail.failedAttempts': function (p) { return 'after ' + p.a + ' attempts' },
  'requestMail.detail.viaAdditional': 'Added for this request',
  'requestMail.detail.redirect': function (p) { return 'Test redirect: delivered to ' + p.address },
  'requestMail.detail.serverMessage': 'Server message',
  'requestMail.detail.serverMessageTitle': 'Mail server message',
  'requestMail.detail.fixHint': 'Fix the address in Emails Update or the user\'s profile.',
  'requestMail.reason.invalid_address': 'Not a valid e-mail address',
  'requestMail.reason.transport': 'The mail server refused it or couldn\'t be reached',
  // D31 (XOL-626): the send job never ran, was lost, failed, or the e-mail never reached the queue.
  'requestMail.reason.not_run': 'Not sent: the send job never started',
  'requestMail.reason.job_lost': 'Not sent: the retries stopped before it went out',
  'requestMail.reason.job_failed': 'Couldn\'t be sent: the send job failed',
  'requestMail.reason.dispatch_failed': 'Not sent: it couldn\'t be queued',
  'requestMail.reason.unknown': 'Couldn\'t be sent',
  'requestMail.footer.close': 'Close',

  // -- XOL-645: the stored copy of a request e-mail (MailPreviewDialog, the View e-mail control) --
  'requestMail.preview.view': 'View e-mail',
  'requestMail.preview.viewAria': function (p) { return 'View the e-mail to ' + p.email },
  'requestMail.preview.title': function (p) { return (p.label || 'E-mail') + ' to ' + p.email },
  'requestMail.preview.notice': 'This e-mail was captured on this server instead of being sent, so nobody received it.',
  'requestMail.preview.meta.subject': 'Subject',
  'requestMail.preview.meta.from': 'From',
  'requestMail.preview.meta.to': 'To',
  'requestMail.preview.meta.captured': 'Captured',
  'requestMail.preview.address': function (p) { return p.name ? p.name + ' (' + p.address + ')' : p.address },
  'requestMail.preview.redirected': function (p) { return p.email + ' (redirected to ' + p.address + ')' },
  'requestMail.preview.noSubject': '(no subject)',
  'requestMail.preview.frameTitle': function (p) { return 'E-mail content' + (p.subject ? ': ' + p.subject : '') },
  'requestMail.preview.loading': 'Loading e-mail…',
  'requestMail.preview.error': 'Couldn\'t load this e-mail.',
  'requestMail.preview.notFound': 'No copy of this e-mail is available. Copies are deleted after a while, and not every e-mail is captured.',
  'requestMail.preview.forbidden': function (p) { return 'Only ' + p.towerCo + ' users can view e-mail copies.' },
  'requestMail.preview.retry': 'Try again',
  'requestMail.preview.close': 'Close',

  // -- XOL-602/603/604: account e-mail (password links, "password changed" notices) -------------
  // Same surfaces as the request e-mail above, for the e-mails the app sends ABOUT an account:
  // the send action (PasswordLinkAction), the history (AccountMailList) and the stored copy
  // (MailPreviewDialog with copyPrefix 'accountMail.preview'). Every key MailPreviewDialog looks
  // up with a copyPrefix is defined here too, because activityText() throws on an unknown key.

  // The action. `invite` = the contact has no password yet; `reset` = they do.
  'accountMail.action.invite': 'Send invitation',
  'accountMail.action.reset': 'Send reset link',
  'accountMail.confirm.title.invite': 'Send invitation?',
  'accountMail.confirm.title.reset': 'Send reset link?',
  'accountMail.confirm.message.invite': function (p) {
    return 'A link to set a password goes to ' + p.email + '. It expires in 3 days.'
  },
  'accountMail.confirm.message.reset': function (p) {
    return 'A reset link goes to ' + p.email + '. It expires in 60 minutes.'
  },
  'accountMail.confirm.capture': 'This server keeps the e-mail for viewing; it is not delivered.',
  'accountMail.confirm.redirect': 'This server sends it to a test address.',
  'accountMail.confirm.cancel': 'Cancel',

  // Why the button is disabled: one line under it, in this order. `.system` = the super's System
  // Details; `.contact*` = the manager's Contact tab (a manager cannot add a Login ID or switch an
  // account on, and can add an e-mail only when the Edit gate lets them).
  'accountMail.reason.unsaved': 'Save your changes first. The link goes to the saved e-mail address.',
  'accountMail.reason.noLogin.contact': 'This contact has no Login ID. Ask a super user to add one.',
  'accountMail.reason.noEmail.system': 'Add an e-mail address on the Contact tab and save first.',
  'accountMail.reason.noEmail.contactEdit': 'Add an e-mail address above and save first.',
  'accountMail.reason.noEmail.contactOther': 'This contact has no e-mail address. Ask a super user to add one.',
  'accountMail.reason.disabled.system': 'Switch on Enabled? and save first. A disabled account can\'t use a link.',
  'accountMail.reason.disabled.contact': 'This account is disabled. Ask a super user to enable it.',
  'accountMail.reason.links': 'This server\'s app address is not set up. Tell Xolas support (code: spa_url_missing).',
  'accountMail.reason.cooldown': 'A link was just sent. You can send another in one minute.',

  // What the click answered (a $message, never a $notify).
  'accountMail.toast.queued.invite': function (p) { return 'Invitation queued for ' + p.email + '.' },
  'accountMail.toast.queued.reset': function (p) { return 'Reset link queued for ' + p.email + '.' },
  'accountMail.toast.captured': 'It will be kept on this server, not delivered.',
  'accountMail.toast.noLogin.system': 'This contact has no Login ID. Add one and save first.',
  'accountMail.toast.noLogin.contact': 'This contact has no Login ID. Ask a super user to add one.',
  'accountMail.toast.noEmail.system': 'This contact has no e-mail address. Add one and save first.',
  'accountMail.toast.noEmail.contactEdit': 'This contact has no e-mail address. Add one above and save first.',
  'accountMail.toast.noEmail.contactOther': 'This contact has no e-mail address. Ask a super user to add one.',
  'accountMail.toast.disabled.system': 'This account is disabled. Switch on Enabled? and save first.',
  'accountMail.toast.disabled.contact': 'This account is disabled. Ask a super user to enable it.',
  'accountMail.toast.linksNotReady': 'This server\'s app address is not set up. Tell Xolas support (code: spa_url_missing).',
  'accountMail.toast.cooldown': 'A link was just sent. Wait one minute before sending another.',
  'accountMail.toast.forbidden': 'You can\'t send password links for this contact.',
  'accountMail.toast.notFound': 'This contact no longer exists. Close it and refresh the list.',
  'accountMail.toast.error': 'Couldn\'t send the link. Try again.',

  // The history (System Details, super).
  'accountMail.list.heading': 'Account e-mails',
  'accountMail.list.refresh': 'Refresh',
  'accountMail.list.refreshAria': 'Refresh account e-mails',
  'accountMail.list.col.email': 'E-mail',
  'accountMail.list.col.status': 'Status',
  'accountMail.list.capture.title': 'This server keeps e-mails instead of sending them.',
  'accountMail.list.capture.description': 'Nobody receives them. Use View e-mail to see what the contact would get.',
  'accountMail.list.redirect.title': 'This server sends e-mails to a test address',
  'accountMail.list.redirect.description': 'The contact does not receive them.',
  'accountMail.list.loading': 'Loading account e-mails…',
  'accountMail.list.empty': 'No account e-mails yet.',
  'accountMail.list.error': 'Couldn\'t load account e-mails.',
  'accountMail.list.retry': 'Try again',
  'accountMail.list.forbidden': 'Only super users can see account e-mails.',
  'accountMail.list.queued': 'Some e-mails are still queued.',
  'accountMail.list.recent': 'Showing the 20 most recent e-mails.',
  'accountMail.list.helper': '“Sent” means the mail server accepted the e-mail. It doesn\'t confirm that it reached the inbox.',
  'accountMail.list.to': function (p) { return 'To ' + p.email },

  // The link beside Send (System Details, super): it opens the history in a slider drawer. On a
  // capture host the e-mails are kept here to preview and nobody receives them, so it says so.
  'accountMail.view.captured': 'View captured e-mails',
  'accountMail.view.live': 'View account e-mails',
  'accountMail.kind.invite': 'Invitation',
  'accountMail.kind.reset_admin': 'Reset link',
  'accountMail.kind.reset_self': 'Reset link',
  'accountMail.kind.password_changed': 'Password changed notice',
  // The preview dialog's header title: the kind, plus " e-mail".
  'accountMail.kind.label.invite': 'Invitation e-mail',
  'accountMail.kind.label.reset_admin': 'Reset link e-mail',
  'accountMail.kind.label.reset_self': 'Reset link e-mail',
  'accountMail.kind.label.password_changed': 'Password changed e-mail',
  // `.sent` is the fallback when the sender no longer exists (requested_by is null).
  'accountMail.who.sentBy': function (p) { return 'Sent by ' + p.name + ' · ' + p.datetime },
  'accountMail.who.sent': function (p) { return 'Sent · ' + p.datetime },
  'accountMail.who.self': function (p) { return 'Asked for on the login page · ' + p.datetime },
  'accountMail.who.changed': function (p) { return 'Sent after the password was changed · ' + p.datetime },
  'accountMail.status.queued': 'Queued',
  'accountMail.status.queued.detail': 'Waiting to be sent',
  'accountMail.status.sent': 'Sent',
  'accountMail.status.sent.detail': function (p) { return 'Sent ' + p.datetime },
  'accountMail.status.captured': 'Captured',
  'accountMail.status.captured.detail': 'Kept on this server, not delivered',
  'accountMail.status.failed': 'Failed',
  'accountMail.status.failed.detail': 'The mail server refused it or couldn\'t be reached',
  'accountMail.status.blocked': 'Not sent',
  'accountMail.status.blocked.detail': 'This server\'s app address is not set up. Tell Xolas support (code: spa_url_missing).',
  'accountMail.link.pending': 'Link not used yet',
  'accountMail.link.used': 'Link used',
  'accountMail.link.expired': 'Link expired',
  'accountMail.link.superseded': 'Link replaced by a newer one',
  // XOL-602 D-6: the API revokes a contact's live links when the contact is disabled.
  'accountMail.link.cancelled': 'Link cancelled: the account was disabled',

  // The stored copy (MailPreviewDialog, copyPrefix 'accountMail.preview'). Same strings as
  // requestMail.preview.* except where a line says otherwise.
  'accountMail.preview.view': 'View e-mail',
  'accountMail.preview.viewAria': function (p) { return 'View the ' + p.kind + ' e-mail sent ' + p.datetime },
  'accountMail.preview.recipientLine': function (p) { return p.name ? p.name + ' · Login ID ' + p.login : 'Login ID ' + p.login },
  'accountMail.preview.title': function (p) { return (p.label || 'E-mail') + ' to ' + p.email },
  'accountMail.preview.notice': 'This e-mail was captured on this server instead of being sent, so nobody received it.',
  // The description under the notice, for an e-mail that carries a link (not "password changed").
  'accountMail.preview.noticeLink': 'The link in it is real. In a browser where you are signed in, it just opens the app. In a private (incognito) window it opens the page to set this contact\'s password, and saving uses up the link.',
  'accountMail.preview.meta.subject': 'Subject',
  'accountMail.preview.meta.from': 'From',
  'accountMail.preview.meta.to': 'To',
  'accountMail.preview.meta.captured': 'Captured',
  'accountMail.preview.address': function (p) { return p.name ? p.name + ' (' + p.address + ')' : p.address },
  'accountMail.preview.redirected': function (p) { return p.email + ' (redirected to ' + p.address + ')' },
  'accountMail.preview.noSubject': '(no subject)',
  'accountMail.preview.frameTitle': function (p) { return 'E-mail content' + (p.subject ? ': ' + p.subject : '') },
  'accountMail.preview.loading': 'Loading e-mail…',
  'accountMail.preview.error': 'Couldn\'t load this e-mail.',
  'accountMail.preview.notFound': 'No copy of this e-mail is available. Copies are deleted after a while, and not every e-mail is captured.',
  'accountMail.preview.forbidden': 'Only super users who are not View Only can view account e-mails.',
  'accountMail.preview.retry': 'Try again',
  'accountMail.preview.close': 'Close',

  // -- XOL-602/603/604: invite by e-mail to register (ContactInviteAction, ContactInvitesPanel) --
  // UX spec §13-§15 and §18, as amended by "UX sign-off: mock Revision 2" (items 9-13, N9, O5,
  // "must not copy" 7). No `contactInvite.*` key says "token", "register invite" or "revoke".
  // The invitation e-mail is reviewed on the panel only (D26): there is deliberately NO
  // `accountMail.kind.register_invite`. `contactInvite.preview.*` is every key MailPreviewDialog
  // looks up under copyPrefix 'contactInvite.preview' (activityText throws on an unknown key).

  // The toolbar button.
  'contactInvite.button': 'Invite by e-mail',

  // The dialog.
  'contactInvite.dialog.title': 'Invite by e-mail',
  'contactInvite.dialog.subtitle': function (p) { return p.org ? 'New account in ' + p.org : 'New account' },
  'contactInvite.dialog.intro': function (p) {
    return 'We will e-mail a link to this address so the person can create their own account in ' + (p.org || 'this organisation') + '. It expires in 3 days.'
  },
  'contactInvite.dialog.email': 'E-mail',
  'contactInvite.dialog.name': 'Name (optional)',
  'contactInvite.dialog.nameHint': 'They can change it when they register.',
  'contactInvite.dialog.cancel': 'Cancel',
  'contactInvite.dialog.send': 'Send invitation',
  'contactInvite.dialog.sending': 'Sending…',

  // The notices at the top of the dialog (dialog) and of the panel (panel). Nothing on a live host.
  'contactInvite.mode.capture.title': 'This server keeps e-mails instead of sending them.',
  'contactInvite.mode.capture.dialog': 'Nobody receives the invitation. Use View e-mail in the Invitations list to see it.',
  'contactInvite.mode.capture.panel': 'Nobody receives the invitations. Use View e-mail to see what the person would get.',
  'contactInvite.mode.redirect.title': 'This server sends e-mails to a test address.',
  'contactInvite.mode.redirect.dialog': 'The person does not receive the invitation.',
  'contactInvite.mode.redirect.panel': 'The invited person does not receive them.',
  'contactInvite.mode.links.title': 'This server\'s app address is not set up.',
  'contactInvite.mode.links.dialog': 'You can\'t send invitations here. Tell Xolas support (code: spa_url_missing).',

  // The access the inviter presets (D20). The three hints are ContactDetail's own descriptions.
  'contactInvite.access.heading': 'Access for the new account',
  'contactInvite.access.defaultView': 'Default View',
  'contactInvite.access.defaultViewPlaceholder': 'None',
  'contactInvite.access.roles': 'User Roles',
  'contactInvite.access.viewer': 'View Only',
  'contactInvite.access.viewerHint': 'View Only can\'t add, edit or delete anything. Only can create tickets.',
  'contactInvite.access.approver': 'Approver',
  'contactInvite.access.approverHint': 'Approver for Requests (can approve and send approval email)',
  'contactInvite.access.manager': 'Manager',
  'contactInvite.access.managerHint': 'Manager can add, edit and delete according to role(s)',
  'contactInvite.access.super': 'Super',
  // ContactDetail's own description of the Super switch.
  'contactInvite.access.superHint': 'Super User can create users and delete anything even sites.',
  // Shown only when an option is disabled: a non-super can give only the access they hold.
  'contactInvite.access.limitedHint': 'You can only give access you have.',
  // XOL-674: the Alerts sub-section names its switches by the kind alone (the heading says "Alerts");
  // the tips say what each one e-mails. ContactDetail reads the same three hints.
  'contactInvite.access.alertsHeading': 'Alerts',
  'contactInvite.access.upload': 'Upload',
  'contactInvite.access.approval': 'Approval',
  'contactInvite.access.finance': 'Finance',
  'contactInvite.access.uploadHint': 'Sends an e-mail when a file is uploaded to a request.',
  'contactInvite.access.approvalHint': 'Sends an e-mail when a request needs approval or a file is approved.',
  'contactInvite.access.financeHint': 'Sends an e-mail when a sharer\'s fee changes tier.',
  // The access line of a panel row: "Access: View Only · Roles: Project, Finance · Default View: Project · Alerts: Upload".
  'contactInvite.access.summary.access': function (p) { return 'Access: ' + p.value },
  'contactInvite.access.summary.roles': function (p) { return 'Roles: ' + p.value },
  'contactInvite.access.summary.defaultView': function (p) { return 'Default View: ' + p.value },
  'contactInvite.access.summary.alerts': function (p) { return 'Alerts: ' + p.value },
  'contactInvite.access.alertUpload': 'Upload',
  'contactInvite.access.alertApproval': 'Approval',
  'contactInvite.access.alertFinance': 'Finance',
  'contactInvite.access.none': 'None',

  // Field checks (the XOL-487/493 :error pattern).
  'contactInvite.validate.emailRequired': 'Enter an e-mail address.',
  'contactInvite.validate.emailFormat': 'Enter a valid e-mail address.',
  'contactInvite.validate.nameShort': 'Use at least 3 characters, or leave it blank.',

  // What the dialog says after Send. Only a success is a toast; the rest stay inside the dialog.
  'contactInvite.result.queued': function (p) { return 'Invitation queued for ' + p.email + '.' },
  'contactInvite.result.resent': function (p) { return 'Invitation sent again to ' + p.email + '.' },
  'contactInvite.result.captured': 'It will be kept on this server, not delivered.',
  'contactInvite.result.rate.title': 'Too many invitations in a short time.',
  'contactInvite.result.rate.description': 'Wait one minute, then try again.',
  'contactInvite.result.forbidden': 'You can\'t send invitations for this organisation.',
  'contactInvite.result.notFound.title': 'This organisation no longer exists.',
  'contactInvite.result.notFound.description': 'Close this window and refresh the list.',
  'contactInvite.result.error.title': 'Couldn\'t send the invitation.',
  'contactInvite.result.error.description': 'Check your connection, then click Send invitation again.',

  // 409 email_in_use: listed, with no "send instead" button (D23). 409 invite_pending.
  'contactInvite.conflict.inUse.one': 'This e-mail address already belongs to a contact',
  'contactInvite.conflict.inUse.many': function (p) { return 'This e-mail address already belongs to ' + p.n + ' contacts' },
  'contactInvite.conflict.inUse.sentence': 'An invitation can\'t be sent to this address. Open the contact and use its Send invitation or Send reset link button instead.',
  'contactInvite.conflict.inUse.ask': 'If the contact is in another organisation, ask a super user.',
  'contactInvite.conflict.pending.title': 'An invitation is already waiting for this address',
  'contactInvite.conflict.pending.same': function (p) {
    return 'It was sent by ' + p.who + (p.datetime ? ' on ' + p.datetime : '') + '. You can send it again. It keeps the access chosen when it was first sent.'
  },
  'contactInvite.conflict.pending.other': function (p) {
    return 'It is for ' + p.org + '. It was sent by ' + p.who + (p.datetime ? ' on ' + p.datetime : '') + '. You can send it again from here, or cancel it in that organisation\'s Contacts tab.'
  },
  // A manager looking at another organisation's invitation: no button (N9).
  'contactInvite.conflict.pending.otherManager': function (p) {
    return 'It is for ' + p.org + '. It was sent by ' + p.who + (p.datetime ? ' on ' + p.datetime : '') + '. Only a super user or a manager of ' + p.org + ' can send it again or cancel it.'
  },
  'contactInvite.conflict.pending.again': 'Send it again',
  'contactInvite.conflict.removedUser': 'a removed user',

  // The Invitations panel.
  'contactInvite.panel.title': 'Invitations',
  // The toolbar button's accessible name: the visible word, plus the waiting count the badge shows.
  'contactInvite.panel.buttonAria': function (p) { return 'Invitations' + (p.waiting > 0 ? ', ' + p.waiting + ' waiting' : '') },
  'contactInvite.panel.refresh': 'Refresh',
  'contactInvite.panel.refreshAria': 'Refresh invitations',
  'contactInvite.panel.loading': 'Loading invitations…',
  'contactInvite.panel.empty': 'No invitations yet. Use Invite by e-mail to send one.',
  'contactInvite.panel.error': 'Couldn\'t load invitations.',
  'contactInvite.panel.retry': 'Try again',
  'contactInvite.panel.forbidden': 'You can\'t see invitations for this organisation.',
  'contactInvite.panel.queued': 'Some e-mails are still queued.',
  'contactInvite.panel.recent': 'Showing the 50 most recent invitations.',
  'contactInvite.panel.helper': '“Sent” means the mail server accepted the e-mail. It doesn\'t confirm that it reached the inbox.',
  'contactInvite.panel.col.invitation': 'Invitation',
  'contactInvite.panel.col.status': 'Status',
  'contactInvite.panel.noName': 'No name given',
  'contactInvite.panel.invitedBy': function (p) {
    return 'Invited by ' + (p.name || 'a removed user') + (p.datetime ? ' · ' + p.datetime : '') + (p.sendCount > 1 ? ' · sent ' + p.sendCount + ' times' : '')
  },
  'contactInvite.panel.account': 'Account:',
  'contactInvite.panel.loginId': function (p) { return 'Login ID ' + p.login },

  // The Status column: a tag (icon + word) and its detail line.
  'contactInvite.status.pending': 'Waiting',
  'contactInvite.status.pending.detail': function (p) { return p.datetime ? 'Expires ' + p.datetime : '' },
  'contactInvite.status.registered': 'Registered',
  'contactInvite.status.registered.detail': function (p) { return p.datetime ? 'Registered ' + p.datetime : 'Account created' },
  'contactInvite.status.expired': 'Expired',
  'contactInvite.status.expired.detail': function (p) { return 'Expired' + (p.datetime ? ' ' + p.datetime : '') + '. To invite again, use Invite by e-mail.' },
  'contactInvite.status.revoked': 'Cancelled',
  'contactInvite.status.revoked.detail': function (p) {
    return p.name ? 'Cancelled by ' + p.name + (p.datetime ? ' · ' + p.datetime : '') : 'Cancelled'
  },
  // The last e-mail of an invitation. Status is never colour alone: each starts with a word.
  'contactInvite.mail.queued': 'E-mail waiting to be sent',
  'contactInvite.mail.sent': function (p) { return p.datetime ? 'E-mail sent ' + p.datetime : 'E-mail sent' },
  'contactInvite.mail.captured': 'E-mail kept on this server, not delivered',
  'contactInvite.mail.failed': 'E-mail failed: the mail server refused it or couldn\'t be reached',
  'contactInvite.mail.blocked': 'E-mail not sent: this server\'s app address is not set up. Tell Xolas support (code: spa_url_missing).',

  // Resend (#9). The confirm is plain text; Esc = Cancel (escCancel.js).
  'contactInvite.resend.action': 'Resend',
  'contactInvite.resend.actionAria': function (p) { return 'Resend the invitation to ' + p.email },
  'contactInvite.resend.title': 'Send the invitation again?',
  'contactInvite.resend.message': function (p) {
    return 'A new link goes to ' + p.email + '. It expires in 3 days.'
  },
  'contactInvite.resend.capture': 'This server keeps the e-mail for viewing; it is not delivered.',
  'contactInvite.resend.redirect': 'This server sends it to a test address.',
  'contactInvite.resend.confirm': 'Send again',
  'contactInvite.resend.cancel': 'Cancel',
  'contactInvite.resend.cooldown': 'Sent just now. You can send it again in one minute.',
  'contactInvite.resend.notPending': 'This invitation is no longer waiting. The list has been refreshed.',
  'contactInvite.resend.tooSoon': 'This invitation was just sent. Wait one minute before sending it again.',
  'contactInvite.resend.forbidden': 'You can\'t send invitations for this organisation.',
  'contactInvite.resend.notFound': 'This invitation no longer exists. The list has been refreshed.',
  'contactInvite.resend.linksNotReady': 'This server\'s app address is not set up. Tell Xolas support (code: spa_url_missing).',
  'contactInvite.resend.error': 'Couldn\'t send the invitation again. Try again.',

  // Cancel invitation (#10). "Keep invitation", not "Cancel": two Cancels in one box are unreadable.
  'contactInvite.cancel.action': 'Cancel invitation',
  'contactInvite.cancel.actionAria': function (p) { return 'Cancel the invitation to ' + p.email },
  'contactInvite.cancel.title': 'Cancel this invitation?',
  'contactInvite.cancel.message': function (p) {
    return 'The link sent to ' + p.email + ' will stop working. You can invite this address again later.'
  },
  'contactInvite.cancel.confirm': 'Cancel invitation',
  'contactInvite.cancel.keep': 'Keep invitation',
  'contactInvite.cancel.done': 'Invitation cancelled.',
  'contactInvite.cancel.forbidden': 'You can\'t cancel invitations for this organisation.',
  'contactInvite.cancel.error': 'Couldn\'t cancel the invitation. Try again.',

  // The stored copy of an invitation e-mail (MailPreviewDialog, copyPrefix 'contactInvite.preview').
  // The same strings as accountMail.preview.* except where a line says otherwise.
  'contactInvite.preview.view': 'View e-mail',
  'contactInvite.preview.viewAria': function (p) { return 'View the invitation e-mail sent to ' + p.email + (p.datetime ? ' on ' + p.datetime : '') },
  'contactInvite.preview.label': 'Invitation e-mail',
  'contactInvite.preview.recipientLine': function (p) { return (p.name || 'No name given') + (p.org ? ' · ' + p.org : '') },
  'contactInvite.preview.title': function (p) { return (p.label || 'E-mail') + ' to ' + p.email },
  'contactInvite.preview.notice': 'This e-mail was captured on this server instead of being sent, so nobody received it.',
  // MailPreviewDialog reads it as `noticeLink` (the one description a linked e-mail has); this is
  // the mock's `noticeRegister` (sign-off item 9), and it names the invited address.
  'contactInvite.preview.noticeLink': function (p) {
    return 'The link in it is real. In a browser where you are signed in, it just opens the app. In a private (incognito) window, filling in the form creates a real account for ' + p.email + ' and uses up the invitation.'
  },
  'contactInvite.preview.meta.subject': 'Subject',
  'contactInvite.preview.meta.from': 'From',
  'contactInvite.preview.meta.to': 'To',
  'contactInvite.preview.meta.captured': 'Captured',
  'contactInvite.preview.address': function (p) { return p.name ? p.name + ' (' + p.address + ')' : p.address },
  'contactInvite.preview.redirected': function (p) { return p.email + ' (redirected to ' + p.address + ')' },
  'contactInvite.preview.noSubject': '(no subject)',
  'contactInvite.preview.frameTitle': function (p) { return 'E-mail content' + (p.subject ? ': ' + p.subject : '') },
  'contactInvite.preview.loading': 'Loading e-mail…',
  'contactInvite.preview.error': 'Couldn\'t load this e-mail.',
  'contactInvite.preview.notFound': 'No copy of this e-mail is available. Copies are deleted after a while, and not every e-mail is captured.',
  'contactInvite.preview.forbidden': 'You can\'t view this invitation e-mail.',
  'contactInvite.preview.retry': 'Try again',
  'contactInvite.preview.close': 'Close',

  // -- The notice of an automatic mail (the `notice` key of an upload / PUT /file) --
  'requestMail.notice.upload.queued': function (p) {
    // Without per-party counts (a notice with no `parties`) it names the total only, as before.
    if (p.parties) {
      return 'Upload notice is being sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + ' — ' + p.parties + '. Delivery status is in Activity.'
    }
    return 'Upload notice is being sent to ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + '.'
  },
  'requestMail.notice.upload.partialTitle': 'Upload notice: not everyone was e-mailed',
  'requestMail.notice.upload.nobody': function (p) {
    return 'Upload saved. No upload notice was sent — nobody ' + (p.orgs ? 'at ' + p.orgs + ' ' : '') + 'gets upload notices.'
  },
  'requestMail.notice.fileApproved.queued': function (p) {
    if (p.parties) {
      return 'File approved. ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + ' ' + plural(p.n, 'is', 'are') + ' being e-mailed — ' + p.parties + '.'
    }
    return 'File approved. ' + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + (p.orgs ? ' at ' + p.orgs : '') + ' ' + plural(p.n, 'is', 'are') + ' being e-mailed.'
  },
  'requestMail.notice.fileApproved.partialTitle': 'File approved: not everyone was e-mailed',
  'requestMail.notice.fileApproved.nobody': function (p) {
    return 'File approved. No e-mail was sent — nobody ' + (p.orgs ? 'at ' + p.orgs + ' ' : '') + 'gets approval e-mails.'
  },

  // XOL-67: the notice of an RTA command (kind 'rta_notice'). `sentence` is the command's own
  // ("Option 2 withdrawn."); the caller words it. `activity` is true for a Tower Co actor, who
  // can read the delivery status; the client gets the counts-only notice (Q9) and no such line.
  'requestMail.notice.rta.queued': function (p) {
    return (p.sentence ? p.sentence + ' ' : '') + p.n + ' ' + plural(p.n, 'recipient', 'recipients') + (p.orgs ? ' at ' + p.orgs : '') + ' ' +
      plural(p.n, 'is', 'are') + ' being e-mailed.' + (p.activity ? ' Delivery status is in Activity.' : '')
  },
  'requestMail.notice.rta.nobody': function (p) {
    return (p.sentence ? p.sentence + ' ' : '') + 'Nobody ' + (p.orgs ? 'at ' + p.orgs + ' ' : '') + 'was e-mailed: nobody there has Approval Alerts on.'
  },
  'requestMail.notice.rta.partialTitle': function (p) {
    return (p.sentence ? String(p.sentence).replace(/\.$/, '') : 'RTA e-mail') + ': not everyone was e-mailed'
  },
  'requestMail.notice.rta.partialFooter.client': function (p) { return 'Ask ' + (p.towerCo || 'the Tower Co') + ' to check the addresses.' },

  // -- Why an address or an organisation was not e-mailed ----------------
  'requestMail.skipped.no_recipients': function (p) { return orgOf(p) + ' (' + p.party + ') — nobody would get this e-mail' },
  'requestMail.skipped.contact_missing': function (p) { return 'A user added under Additional for ' + orgOf(p) + ' no longer exists' },
  'requestMail.skipped.contact_disabled': function (p) { return p.name + ' (' + orgOf(p) + ') — their user account is disabled' },
  'requestMail.skipped.other': function (p) { return orgOf(p) + ' (' + p.party + ') — not e-mailed' },

  // -- The Activity log: containers, filters, states (XOL-586 NEW-B) ------
  'requestActivity.trigger': 'Activity',
  'requestActivity.heading': function (p) { return 'Activity: ' + p.rtx + ' #' + p.id },
  'requestActivity.region': 'Request activity',
  'requestActivity.filter.step.label': 'Step',
  'requestActivity.filter.step.all': 'All steps',
  'requestActivity.filter.step.option': function (p) { return 'Step ' + p.n + (p.title ? ': ' + p.title : '') },
  'requestActivity.filter.type.label': 'Type',
  'requestActivity.filter.type.all': 'All types',
  'requestActivity.filter.type.mail': 'E-mails & recipients',
  'requestActivity.filter.type.status': 'Status & approvals',
  'requestActivity.filter.type.fields': 'Field changes',
  'requestActivity.filter.type.files': 'Files',
  'requestActivity.refresh': 'Refresh',
  'requestActivity.refresh.aria': 'Refresh activity',
  'requestActivity.day.today': 'Today',
  'requestActivity.day.yesterday': 'Yesterday',
  // The accessible name of a row that opens a dialog (Delta 4): the sentence, its result, who, when, step.
  'requestActivity.entry.aria': function (p) {
    return p.text + (p.tag ? ' (' + p.tag + ')' : '') + '. ' + p.who + ', ' + p.when + '. ' + p.step + '.'
  },
  'requestActivity.loading': 'Loading activity…',
  'requestActivity.empty.title': 'No activity recorded for this request yet',
  'requestActivity.empty.body': 'Changes, approvals, files and e-mails are listed here as they happen. Changes made before the activity log was introduced are not listed.',
  'requestActivity.emptyFiltered.title': 'No activity matches these filters',
  'requestActivity.emptyFiltered.clear': 'Clear filters',
  'requestActivity.error.title': 'Couldn\'t load the activity log',
  'requestActivity.error.body': 'Check your connection, then try again.',
  'requestActivity.error.forbidden': function (p) { return 'Only ' + p.towerCo + ' users can see this request\'s activity.' },
  'requestActivity.error.retry': 'Try again',
  'requestActivity.end.partial': 'The activity log started after this request was created. Earlier changes and e-mails are not listed.',
  'requestActivity.status.range': function (p) {
    return 'Showing ' + p.from + ' to ' + p.to + ' of ' + p.total + ' entries.'
  },
  'requestActivity.actor.system': 'System',
  'requestActivity.actor.withOrg': function (p) { return p.name + ' (' + p.org + ')' },
  'requestActivity.actor.unknown': 'Unknown user',
  'requestActivity.step.chip': function (p) { return 'Step ' + p.n },
  'requestActivity.step.chipParty': function (p) { return 'Step ' + p.n + ' · ' + p.party },
  'requestActivity.step.request': 'Request',

  // -- One line per kind (what the Activity row says) ---------------------
  'requestActivity.kind.request_created': function (p) { return 'Created this ' + p.rtx },
  'requestActivity.kind.fields_changed.one': function (p) { return 'Changed ' + p.a },
  'requestActivity.kind.fields_changed.two': function (p) { return 'Changed ' + p.a + ' and ' + p.b },
  'requestActivity.kind.fields_changed.three': function (p) { return 'Changed ' + p.a + ', ' + p.b + ' and ' + p.c },
  'requestActivity.kind.fields_changed.many': function (p) { return 'Changed ' + p.a + ', ' + p.b + ' and ' + p.n + ' other fields' },
  'requestActivity.kind.recipients_changed.additional.added': function (p) {
    return 'Added ' + p.n + ' ' + p.org + ' ' + plural(p.n, 'recipient', 'recipients') + ' under Additional'
  },
  'requestActivity.kind.recipients_changed.additional.removed': function (p) {
    return 'Removed ' + p.n + ' ' + p.org + ' ' + plural(p.n, 'recipient', 'recipients') + ' from Additional'
  },
  'requestActivity.kind.recipients_changed.additional.changed': function (p) {
    return 'Changed the ticks of ' + p.n + ' ' + p.org + ' Additional ' + plural(p.n, 'recipient', 'recipients')
  },
  'requestActivity.kind.recipients_changed.additional.mixed': function (p) {
    return 'Updated ' + p.org + ' Additional recipients: ' + p.parts
  },
  'requestActivity.kind.recipients_changed.additional.part.added': function (p) { return p.n + ' added' },
  'requestActivity.kind.recipients_changed.additional.part.removed': function (p) { return p.n + ' removed' },
  'requestActivity.kind.recipients_changed.additional.part.changed': function (p) { return p.n + ' changed' },
  'requestActivity.kind.recipients_changed.unsubscribed.added': function (p) {
    return 'Unsubscribed ' + p.n + ' ' + p.org + ' ' + plural(p.n, 'user', 'users')
  },
  'requestActivity.kind.recipients_changed.unsubscribed.removed': function (p) {
    return 'Re-subscribed ' + p.n + ' ' + p.org + ' ' + plural(p.n, 'user', 'users')
  },
  'requestActivity.kind.recipients_changed.unsubscribed.changed': function (p) {
    return 'Changed the ticks of ' + p.n + ' unsubscribed ' + p.org + ' ' + plural(p.n, 'user', 'users')
  },
  'requestActivity.kind.recipients_changed.unsubscribed.mixed': function (p) {
    return 'Updated ' + p.org + ' Unsubscribed users: ' + p.parts
  },
  'requestActivity.kind.recipients_changed.unsubscribed.part.added': function (p) { return p.n + ' unsubscribed' },
  'requestActivity.kind.recipients_changed.unsubscribed.part.removed': function (p) { return p.n + ' re-subscribed' },
  'requestActivity.kind.recipients_changed.unsubscribed.part.changed': function (p) { return p.n + ' changed' },
  'requestActivity.kind.recipients_changed.noNet': function (p) {
    return 'Edited ' + p.org + ' ' + p.list + ' (no net change)'
  },
  'requestActivity.kind.recipients_changed.list.additional': 'Additional recipients',
  'requestActivity.kind.recipients_changed.list.unsubscribed': 'Unsubscribed users',
  'requestActivity.kind.mail_turned_on': function (p) { return 'Turned e-mails on for this ' + p.rtx },
  'requestActivity.kind.mail_turned_off': function (p) { return 'Turned e-mails off for this ' + p.rtx },
  // XOL-626 D30: the request-scoped sharer writes. Keyed by role (see SHARER_KIND), not by the API's kind name.
  'requestActivity.kind.sharer.created': function (p) { return p.no ? 'Created sharer #' + p.no : 'Created the sharer' },
  // One rule for a named list (sharer_updated and sharer_finance_changed): "the sharer's" goes before the
  // list unless its first label already starts with "Sharer" ("Changed Sharer No.", not "the sharer's Sharer No.").
  'requestActivity.kind.sharer.named': function (p) { return 'Changed ' + (/^Sharer/.test(p.list) ? '' : 'the sharer\'s ') + p.list },
  'requestActivity.kind.sharer.shared': 'Shared the rate with the telco',
  'requestActivity.kind.sharer.unshared': 'Stopped sharing the rate with the telco',
  'requestActivity.kind.sharer.linked': function (p) {
    return (p.no ? 'Linked sharer #' + p.no : 'Linked the sharer') + ' to this ' + p.rtx + (p.reset ? ', and turned off rate sharing' : '')
  },
  'requestActivity.kind.sharer.finance.rate_override_set': 'Set a rate override',
  'requestActivity.kind.sharer.finance.rate_override_cleared': 'Cleared the rate override',
  'requestActivity.kind.sharer.finance.rate_version': 'Changed the rate version',
  'requestActivity.kind.sharer.finance.fields': 'Changed the sharer\'s rate and fees',
  'requestActivity.kind.sharer.list.one': function (p) { return p.a },
  'requestActivity.kind.sharer.list.two': function (p) { return p.a + ' and ' + p.b },
  'requestActivity.kind.sharer.list.three': function (p) { return p.a + ', ' + p.b + ' and ' + p.c },
  'requestActivity.kind.sharer.list.many': function (p) { return p.a + ', ' + p.b + ' and ' + p.n + ' other fields' },
  'requestActivity.kind.approval_decided.set': function (p) { return 'Set the approval to ' + p.to },
  'requestActivity.kind.approval_decided.reset': 'Reset the approval to Pending',
  'requestActivity.kind.approval_decided.dateOnly': 'Changed the approval date',
  'requestActivity.kind.approval_decided.changed': function (p) { return 'Changed the approval from ' + p.from + ' to ' + p.to },
  'requestActivity.kind.approval_decided.dated': function (p) { return ' (dated ' + p.date + ')' },
  'requestActivity.kind.approval_decided.approvedBool': 'Approved this step',
  'requestActivity.kind.approval_decided.removedBool': 'Removed the approval',
  'requestActivity.kind.approval_locked': function (p) { return 'Locked the approval' + (p.status ? ' (' + p.status + ')' : '') },
  'requestActivity.kind.approval_unlocked': 'Unlocked the approval',
  'requestActivity.kind.step_completed': 'Completed this step',
  'requestActivity.kind.step_reopened': 'Re-opened this step',
  'requestActivity.kind.request_completed': function (p) { return 'Completed this ' + p.rtx + (p.status ? '. Status: ' + p.status : '') },
  'requestActivity.kind.request_rejected': function (p) { return 'Closed this ' + p.rtx + ' permanently' + (p.status ? '. Status: ' + p.status : '') },
  'requestActivity.kind.category_added': function (p) { return 'Added the file category “' + p.title + '”' },
  'requestActivity.kind.category_removed': function (p) { return 'Removed the file category “' + p.title + '”' },
  'requestActivity.kind.file_uploaded': function (p) { return 'Uploaded “' + p.name + '” to ' + p.title },
  'requestActivity.kind.file_uploaded.noTitle': function (p) { return 'Uploaded “' + p.name + '”' },
  'requestActivity.kind.file_replaced': function (p) { return 'Replaced the file in ' + p.title + ' with “' + p.name + '”' },
  'requestActivity.kind.file_replaced.noTitle': function (p) { return 'Replaced the file with “' + p.name + '”' },
  'requestActivity.kind.file_removed': function (p) { return 'Removed the file “' + p.name + '”' },
  'requestActivity.kind.file_approved': function (p) { return 'Approved “' + p.name + '”' },
  'requestActivity.kind.file_unapproved': function (p) { return 'Removed the approval of “' + p.name + '”' },
  'requestActivity.kind.file_renamed': function (p) { return 'Renamed “' + p.from + '” to “' + p.to + '”' },
  'requestActivity.kind.file_details_changed': function (p) { return 'Edited the details of “' + p.name + '”' },
  'requestActivity.kind.file_first_download': function (p) { return 'Downloaded “' + p.name + '” for the first time' },
  // XOL-650: a download made through the e-mail link (details.via === 'email'). file_downloaded is
  // only ever written for those, so its copy always says so. Not "again": the earlier download
  // may have been someone else's.
  'requestActivity.kind.file_first_download.viaEmail': function (p) { return 'Downloaded “' + p.name + '” for the first time, from an e-mail link' },
  'requestActivity.kind.file_downloaded': function (p) { return 'Downloaded “' + p.name + '” from an e-mail link' },
  'requestActivity.kind.other': 'Other change',

  // -- XOL-67: one line per RTA kind (UX v4 section 3). Option numbers are parts of the sentence,
  // so the sentence builders below take them from the row; these are the fixed wordings. --------
  'requestActivity.kind.rta.created.for': 'Created this RTA for ',
  'requestActivity.kind.rta.completed.lead': 'Completed the RTA: created ',
  'requestActivity.kind.rta.completed.noName': 'Completed the RTA: site created',
  'requestActivity.kind.rta.completed.rts': ' and an RTS opened',
  // XOL-693 (decision 211): the RTS was linked, or the RTA completed without one (details.rtsOutcome).
  'requestActivity.kind.rta.completed.rtsLinked': ' and the existing RTS linked',
  'requestActivity.kind.rta.completed.rtsNone': ' with no RTS',
  'requestActivity.kind.rta.rejected': 'Rejected the RTA',
  'requestActivity.kind.rta.rejected.lead': 'Rejected the RTA: ',
  // After the site's name, which is the bold object: "{Site name} ({Site ID}) was deleted by {name}. ..."
  'requestActivity.kind.rta.siteDeleted': function (p) {
    // A name that ends in a full stop ("Aisyah R.") must not get a second one.
    return (p.siteId ? ' (' + p.siteId + ')' : '') + ' was deleted' + (p.by ? ' by ' + p.by + (/\.$/.test(p.by) ? '' : '.') : '.') + ' This RTA and its history are kept.'
  },
  'requestActivity.kind.rta.siteDeleted.noName': 'The site was deleted. This RTA and its history are kept.',
  'requestActivity.kind.rta.decision.set': 'Set the decision to ',
  'requestActivity.kind.rta.decision.onOption': function (p) { return ' on option ' + p.n },
  'requestActivity.kind.rta.decision.changed': function (p) { return 'Changed the decision from ' + p.from + ' to ' },
  'requestActivity.kind.rta.decision.reset': 'Reset the decision to Pending',
  'requestActivity.kind.rta.decision.dateOnly': 'Changed the decision date',
  'requestActivity.kind.rta.decision.remarksOnly': 'Changed the Decision Remarks',
  'requestActivity.kind.rta.decision.andRemarks': ' and changed the Decision Remarks',
  // v2 (API-7, API-9): the saved decision is cleared by a review submit on the approved option or by the Step 3 Decline.
  'requestActivity.kind.rta.reset.submit': function (p) { return 'The saved decision was cleared: ' + p.option + '\'s review changed' },
  'requestActivity.kind.rta.reset.decline': function (p) { return 'The saved decision was cleared: ' + p.option + ' was declined' },
  'requestActivity.kind.rta.reset.other': 'The saved decision was cleared',
  'requestActivity.kind.rta.unlocked': 'Unlocked the decision; the RTA is back in Step 4',
  'requestActivity.kind.rta.transitioned.tail': ' goes to Finalize',
  'requestActivity.kind.rta.added.backToDraft': '; the RTA went back to Searching',
  'requestActivity.kind.rta.reviewed.cleared': '; the saved decision was cleared',
  'requestActivity.kind.rta.reviewed.backToDraft': '; every option is declined, so the RTA went back to Searching',
  // XOL-67 (decisions 157, 158 Q4; API P-S29; UX spec 4.8): a field edit of the request or of an option is a `fields_changed` row whose `changed` is
  // { path: { from, to } }. "Changed **Antenna AGL** on the request: 28 m → 32 m" · "Changed **Structure Height** on option 2: 30 m → 36 m" ·
  // "Moved the **requested point** (Lat / Lng)" · "Moved **option 2**'s pin (Lat / Lng)". An empty value is "not set".
  'requestActivity.kind.rta.fields.lead': 'Changed ',
  'requestActivity.kind.rta.fields.onRequest': ' on the request',
  'requestActivity.kind.rta.fields.on': function (p) { return ' on ' + p.option },
  'requestActivity.kind.rta.fields.change': function (p) { return p.from + ' → ' + p.to },
  'requestActivity.kind.rta.fields.notSet': 'not set',
  'requestActivity.kind.rta.fields.point': 'Lat / Lng',
  'requestActivity.kind.rta.fields.moved.lead': 'Moved ',
  'requestActivity.kind.rta.fields.moved.request.lead': 'Moved the ',
  'requestActivity.kind.rta.fields.moved.request.obj': 'requested point',
  'requestActivity.kind.rta.fields.moved.request.tail': ' (Lat / Lng)',
  'requestActivity.kind.rta.fields.moved.option.tail': '\'s pin (Lat / Lng)',
  'requestActivity.kind.rta.fields.markers.none': 'no markers',
  'requestActivity.kind.rta.fields.markers.count': function (p) { return p.n + ' ' + p.type },

  // -- The field-change dialog (GET /actions/{id}/activity/{activity}) ----
  'requestActivity.changes.title.fields': 'Field changes',
  'requestActivity.changes.title.recipients': 'Recipient changes',
  'requestActivity.changes.meta.who': 'Who',
  'requestActivity.changes.meta.when': 'When',
  'requestActivity.changes.meta.step': 'Step',
  'requestActivity.changes.loading': 'Loading changes…',
  'requestActivity.changes.error': 'Couldn\'t load these changes.',
  'requestActivity.changes.notFound': 'This entry wasn\'t found. Refresh the activity log and try again.',
  'requestActivity.changes.retry': 'Try again',
  'requestActivity.changes.none': 'No saved values for this entry.',
  'requestActivity.changes.coalesced': function (p) {
    return p.n + ' saves made within a few minutes are merged into this entry. Before shows the value before the first save; After shows the value after the last.'
  },
  'requestActivity.changes.before': 'Before',
  'requestActivity.changes.after': 'After',
  'requestActivity.changes.empty': '(empty)',
  'requestActivity.changes.changedBack': 'Changed back',
  'requestActivity.changes.changedBack.note': 'This was changed and then changed back to its original value.',
  'requestActivity.changes.group.added': 'Added',
  'requestActivity.changes.group.removed': 'Removed',
  'requestActivity.changes.group.unsubscribed': 'Unsubscribed',
  'requestActivity.changes.group.resubscribed': 'Re-subscribed',
  'requestActivity.changes.group.ticks': 'Ticks changed',
  'requestActivity.changes.groupCount': function (p) { return p.label + ' (' + p.n + ')' },
  'requestActivity.changes.entry.contact': function (p) { return p.name + ' (' + p.email + ')' },
  'requestActivity.changes.entry.typed': function (p) { return p.email + ' (typed address)' },
  'requestActivity.changes.entry.withTicks': function (p) { return p.who + ' — ' + p.ticks },
  'requestActivity.changes.entry.ticks': function (p) { return p.who + ': ' + p.before + ' → ' + p.after },
  'requestActivity.changes.entry.unknown': 'A listed user',
  'requestActivity.changes.ticks.upload': 'Upload',
  'requestActivity.changes.ticks.approval': 'Approval',
  'requestActivity.changes.ticks.both': 'Upload and Approval',
  'requestActivity.changes.list.aria': function (p) { return p.label + ': ' + p.group },
  'requestActivity.changes.noNetChange': 'The list was changed and then changed back.',
  'requestActivity.changes.close': 'Close',
  'requestActivity.changes.remarksAria': function (p) { return p.label + ' ' + p.side }
}

/**
 * The text of a key. A template function takes ONE params object. Throws on an
 * unknown key, so a typo fails a spec instead of rendering a blank.
 */
export function activityText (key, params) {
  if (!Object.prototype.hasOwnProperty.call(COPY, key)) {
    throw new Error('requestActivity: unknown copy key ' + key)
  }
  var entry = COPY[key]
  return typeof entry === 'function' ? entry(params || {}) : entry
}

/* ------------------------------------------------------------------------
   The result of POST /actions/{id}/notify, as the toast the user sees
   (UX spec "Toast copy for trigger results")
------------------------------------------------------------------------ */

function emailsText (list) {
  var emails = (list || []).map(function (f) { return f && f.email ? f.email : null }).filter(Boolean)
  if (emails.length <= 3) {
    return emails.join(', ')
  }
  return emails.slice(0, 3).join(', ') + ' and ' + (emails.length - 3) + ' more'
}

function orgName (o) {
  return o ? (o.shortName || o.name || null) : null
}

// Delta 1 (item 3): a user with no e-mail address is not reported. The API stopped
// emitting `no_email` skips, but a batch written before that still carries them, and
// the SPA must not show them. Every list of skipped items goes through this first.
export function reportedSkips (items) {
  return (items || []).filter(function (s) { return !s || s.reason !== 'no_email' })
}

// The `requestMail.skipped.*` line of each skipped item. An item is
// { party, organisation:{name, shortName}, contact:{name}|null, reason } (the contract
// gives every one an organisation, plus a contact for the contact-level reasons).
export function skippedLines (items, ctx) {
  ctx = ctx || {}

  return reportedSkips(items).map(function (s) {
    var key = 'requestMail.skipped.' + s.reason
    if (!Object.prototype.hasOwnProperty.call(COPY, key)) {
      key = 'requestMail.skipped.other'
    }
    return activityText(key, {
      org: orgName(s.organisation),
      party: Object.prototype.hasOwnProperty.call(COPY, 'requestMail.party.' + s.party) ? activityText('requestMail.party.' + s.party) : s.party,
      name: s.contact && s.contact.name ? s.contact.name : 'A user',
      rtx: ctx.rtx || 'request'
    })
  })
}

// The same lines, joined with "; " for a toast.
function skippedText (items, ctx) {
  return skippedLines(items, ctx).join('; ')
}

/* ------------------------------------------------------------------------
   Delta 3: how many addresses each party was e-mailed, "TLB (Telco) 8 · BHG (Provider) 0".
   A skipped party is not a warning: it is a party with 0, listed as plain text.
------------------------------------------------------------------------ */

var PARTY_RANK = { towerco: 0, owner: 1, provider: 2 }

function partyRank (party) {
  return Object.prototype.hasOwnProperty.call(PARTY_RANK, party) ? PARTY_RANK[party] : 3
}

function sortedParties (rows) {
  return rows.map(function (r, i) { return { r: r, i: i } }).sort(function (a, b) {
    return (partyRank(a.r.party) - partyRank(b.r.party)) || (a.i - b.i)
  }).map(function (x) { return x.r })
}

/**
 * The party rows of one e-mail, [{ party, org, n }], in the order Tower Co, Telco (owner),
 * Provider. `src` is an API answer: the notify body, a notice or a batch object.
 *
 * Uses the API's `parties` ([{party, organisation:{name, shortName}, recipients}]) when it
 * has any. Without them (an older API) the counts come from `recipients` grouped by party
 * (the addresses queued or sent; a failed one is on its own line), plus every party of
 * `skipped` and `failed` at 0. With neither `parties` nor `recipients`, [] (the caller shows
 * the total only).
 */
export function partyCounts (src) {
  src = src || {}

  if (Array.isArray(src.parties) && src.parties.length > 0) {
    return sortedParties(src.parties.filter(Boolean).map(function (p) {
      return { party: p.party, org: orgName(p.organisation), n: num(p.recipients) }
    }))
  }

  if (!Array.isArray(src.recipients)) {
    return []
  }

  var rows = []
  var byParty = {}
  var touch = function (party, organisation) {
    if (!party) return null

    if (!Object.prototype.hasOwnProperty.call(byParty, party)) {
      byParty[party] = { party: party, org: null, n: 0 }
      rows.push(byParty[party])
    }
    if (!byParty[party].org) {
      byParty[party].org = orgName(organisation)
    }

    return byParty[party]
  }

  src.recipients.forEach(function (r) {
    var row = r ? touch(r.party, r.organisation) : null

    if (row && r.status !== 'failed') {
      row.n++
    }
  })
  reportedSkips(src.skipped).forEach(function (s) { if (s) touch(s.party, s.organisation) })
  ;(src.failed || []).forEach(function (f) { if (f) touch(f.party, f.organisation) })

  return sortedParties(rows)
}

// "TLB (Telco) 8 · BHG (Provider) 0", or '' when the answer carries no party counts.
export function partyCountsText (src) {
  return partyCounts(src).map(function (r) {
    var party = Object.prototype.hasOwnProperty.call(COPY, 'requestMail.party.' + r.party) ? activityText('requestMail.party.' + r.party) : (r.party || '')

    return activityText('requestMail.parties.item', {
      label: activityText('requestMail.parties.label', { org: r.org, party: party }),
      n: r.n
    })
  }).join(' · ')
}

function labelOf (event, ctx) {
  if (event === 'test') {
    return activityText('requestMail.notify.label.test')
  }
  if (event === 'rta_decision') {
    return ctx.status
      ? activityText('requestMail.notify.label.rtaDecision', { status: ctx.status })
      : activityText('requestMail.notify.label.rtaDecision.bare')
  }
  return activityText('requestMail.notify.label.approval', { status: ctx.status || 'Approval' })
}

function alertsText (event, ctx) {
  if (event === 'test') {
    return ctx.party === 'towerco'
      ? activityText('requestMail.alerts.upload')
      : activityText('requestMail.alerts.upload') + ' or ' + activityText('requestMail.alerts.approval')
  }
  return activityText('requestMail.alerts.approval')
}

/**
 * @param {String} event  'approval_notice' | 'rta_decision' (XOL-67: the Tower Co decision of an RTA, which has
 *                        no step: ctx.status is its decision) | 'test'
 * @param {Object} ctx    { rtx, status (approval), stepNo (approval), party (test),
 *                          org (test: the party's short name), towerCo, repeat }
 * @param {Object} result { ok: true, data } for a 200, or { ok: false, status, code,
 *                          data, message } for a refusal
 * @return {Object} a toast: { channel: 'message' | 'notify', type, title?, message,
 *                  lines?, duration? }. A partial result carries one `lines` entry per
 *                  sentence (the caller stacks them); `message` is the same text on
 *                  one line.
 */
export function notifyOutcome (event, ctx, result) {
  ctx = ctx || {}
  var label = labelOf(event, ctx)
  var data = (result && result.data && typeof result.data === 'object') ? result.data : {}
  var notSent = activityText('requestMail.notify.title.notSent')

  if (result && result.ok) {
    if (data.duplicate) {
      return { channel: 'message', type: 'info', message: activityText('requestMail.notify.duplicate') }
    }

    var queued = Number(data.queued) || 0
    var failed = data.failed || []

    // Delta 3: a skipped party (nobody there to e-mail) is not a warning. Only an address
    // that could not be used (`failed`) makes the toast one.
    if (failed.length === 0) {
      if (event === 'test') {
        return { channel: 'message', type: 'success', message: activityText('requestMail.test.queued', { n: queued, org: ctx.org }) }
      }
      // Delta 1 (item 4): a Re-send to selected recipients says so. Only the server knows it
      // was a subset (`subset` on the 200), so the toast reads it from there.
      var key = data.subset ? 'requestMail.notify.requeuedSubset' : (ctx.repeat ? 'requestMail.notify.requeued' : 'requestMail.notify.queued')

      return {
        channel: 'message',
        type: 'success',
        message: activityText(key, { label: label, n: queued, parties: partyCountsText(data) })
      }
    }

    var lines = []
    if (queued > 0) {
      lines.push(activityText('requestMail.notify.partial.queued', { n: queued }))
    }
    if (failed.length > 0) {
      lines.push(activityText('requestMail.notify.partial.failed', { f: failed.length, emails: emailsText(failed) }))
    }
    lines.push(activityText('requestMail.notify.partial.footer'))

    return {
      channel: 'notify',
      type: 'warning',
      title: activityText('requestMail.notify.partial.title', { label: label }),
      lines: lines,
      message: lines.join(' '),
      duration: 0
    }
  }

  var status = result ? result.status : null
  var code = result ? result.code : null

  if (status === 409 && code === 'invalid_state') {
    return {
      channel: 'notify',
      type: 'warning',
      title: notSent,
      message: event === 'rta_decision'
        ? activityText('requestMail.notify.invalidState.rta')
        : activityText('requestMail.notify.invalidState', { n: ctx.stepNo }),
      duration: 5500
    }
  }
  if (status === 409 && code === 'request_closed') {
    return { channel: 'notify', type: 'warning', title: notSent, message: activityText('requestMail.notify.requestClosed', { rtx: ctx.rtx }), duration: 0 }
  }
  // Delta 1 (item 1): no toast for "e-mails are off". The Send button is disabled while
  // they are, the request banner says why, and a stale tab re-reads the request.
  if (status === 422 && code === 'mail_off') {
    return null
  }
  // Delta 1 (item 4): the Re-send picker answers these two itself, inline. A stale preview
  // (409) refreshes the list and shows its notice; an empty selection (422) cannot happen
  // because the picker's Send is disabled at 0. Nothing was sent in either case.
  if ((status === 409 && code === 'preview_stale') || (status === 422 && code === 'empty_selection')) {
    return null
  }
  if (status === 422 && (code === 'no_recipients' || code === 'nothing_queued')) {
    if (event === 'test') {
      return { channel: 'notify', type: 'warning', title: notSent, message: activityText('requestMail.test.noRecipients', { org: ctx.org }), duration: 5500 }
    }
    if (code === 'nothing_queued') {
      return {
        channel: 'notify',
        type: 'warning',
        title: notSent,
        message: activityText('requestMail.notify.nothingQueued', { f: (data.failed || []).length, emails: emailsText(data.failed) }),
        duration: 0
      }
    }
    return {
      channel: 'notify',
      type: 'warning',
      title: notSent,
      message: activityText('requestMail.notify.noRecipients', { skippedList: skippedText(data.skipped, ctx), alerts: alertsText(event, ctx) }),
      duration: 0
    }
  }
  if (status === 403) {
    return { channel: 'notify', type: 'error', title: notSent, message: activityText('requestMail.notify.forbidden', { towerCo: ctx.towerCo || 'the Tower Co' }), duration: 5500 }
  }
  if (status === 429) {
    return { channel: 'notify', type: 'warning', title: notSent, message: activityText('requestMail.notify.tooMany'), duration: 5500 }
  }

  return {
    channel: 'notify',
    type: 'error',
    title: notSent,
    message: activityText('requestMail.notify.fallback', { message: result && result.message ? result.message : null }),
    duration: 5500
  }
}

/* ------------------------------------------------------------------------
   The notice of an AUTOMATIC mail (XOL-586 / XOL-590): the `notice` key on an upload
   response and on the Tower Co's PUT /file/{id}
------------------------------------------------------------------------ */

function num (v) {
  var n = Number(v)
  return isNaN(n) ? 0 : n
}

// 'A', 'A and B', 'A, B and C'.
function joinNames (names) {
  if (names.length <= 1) return names.join('')
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}

function uniqueNames (names) {
  var out = []
  names.forEach(function (n) {
    if (n && out.indexOf(n) === -1) out.push(n)
  })
  return out
}

/**
 * The toast of a `notice` ({batch_id, kind, queued, skipped[], failed[], status?, reason?}),
 * or null when there is nothing to say (a null notice: a site file, a replayed echo).
 *
 * XOL-67 (Q9): a caller who is NOT the Tower Co gets a COUNTS-ONLY notice, so that no Tower Co
 * address reaches a Telco: { kind, queued, skipped: 2, failed: 1 } (numbers, not lists), no
 * `batch_id` and no `parties`. It reads like a full one without the names: a clean send, "nobody"
 * and a partial send all work, and a partial one says how many addresses failed, not which.
 *
 * @param {Object} notice
 * @param {Object} ctx { kind: 'upload_notice' | 'file_approved' | 'rta_notice' (when the notice has no
 *                       `kind`), rtx, orgs: [names] }. `orgs` are the organisations the
 *                       notice is addressed to, named in "nobody at {orgs}" and "{n}
 *                       recipients at {orgs}"; the notice itself carries none for a
 *                       clean send, so the caller derives them from the request.
 *                       For an RTA (kind 'rta_notice') also: `sentence` (the command's own
 *                       sentence, "Option 2 withdrawn."), `actor` ('towerco' | 'client'; a notice
 *                       with no `batch_id` and no `parties` is the client's), `towerCo` (its name).
 * @return {Object|null} a toast, as notifyOutcome's: { channel, type, title?, message,
 *                       lines?, duration? }
 */
export function noticeOutcome (notice, ctx) {
  if (!notice || typeof notice !== 'object') {
    return null
  }

  ctx = ctx || {}

  var kind = notice.kind || ctx.kind
  var rta = kind === 'rta_notice'
  var base = rta ? 'requestMail.notice.rta' : (kind === 'file_approved' ? 'requestMail.notice.fileApproved' : 'requestMail.notice.upload')
  var queued = num(notice.queued)
  var skipped = reportedSkips(Array.isArray(notice.skipped) ? notice.skipped : [])
  // The addresses that failed: a list for the Tower Co, a bare count for anyone else.
  var failed = Array.isArray(notice.failed) ? notice.failed : []
  var failedCount = Array.isArray(notice.failed) ? notice.failed.length : num(notice.failed)
  var sentence = rta ? (ctx.sentence || null) : null
  // A Tower Co actor reads the delivery status in Activity; the client's counts-only notice says nothing of it.
  var towerCoActor = ctx.actor ? ctx.actor === 'towerco' : (present(notice.batch_id) || Array.isArray(notice.parties))

  // The organisations named: those the server says had nobody, else the request's own.
  var skippedOrgs = uniqueNames(skipped.map(function (s) { return orgName(s.organisation) }))
  var orgs = joinNames(skippedOrgs.length > 0 ? skippedOrgs : uniqueNames(ctx.orgs || []))

  if (notice.status === 'not_sent') {
    // Delta 1 (item 1): nothing to say. The new file in the table (an upload) or the generic
    // save message (an approval) is the confirmation, and the Activity row keeps the audit.
    if (notice.reason === 'mail_off') {
      return null
    }
    return { channel: 'message', type: 'info', message: activityText(base + '.nobody', { orgs: orgs, sentence: sentence }) }
  }

  // Delta 3: nothing queued and nothing failed means nobody was e-mailed, whatever the
  // skipped parties were; that is said as a plain note, not a warning.
  if (queued === 0 && failedCount === 0) {
    return { channel: 'message', type: 'info', message: activityText(base + '.nobody', { orgs: orgs, sentence: sentence }) }
  }

  // A skipped party never makes the toast a warning; only an address that failed does.
  if (failedCount === 0) {
    return {
      channel: 'message',
      type: 'success',
      message: activityText(base + '.queued', {
        n: queued,
        orgs: joinNames(uniqueNames(ctx.orgs || [])),
        parties: partyCountsText(notice),
        sentence: sentence,
        activity: towerCoActor
      })
    }
  }

  var lines = []
  if (queued > 0) {
    lines.push(activityText('requestMail.notify.partial.queued', { n: queued }))
  }
  if (failed.length > 0) {
    lines.push(activityText('requestMail.notify.partial.failed', { f: failed.length, emails: emailsText(failed) }))
  } else {
    lines.push(activityText('requestMail.notify.partial.failedCount', { f: failedCount }))
  }
  lines.push(rta && !towerCoActor
    ? activityText('requestMail.notice.rta.partialFooter.client', { towerCo: ctx.towerCo })
    : activityText('requestMail.notify.partial.footer'))

  return {
    channel: 'notify',
    type: 'warning',
    title: activityText(base + '.partialTitle', { sentence: sentence }),
    lines: lines,
    message: lines.join(' '),
    duration: 0
  }
}

/* ------------------------------------------------------------------------
   One e-mail event, in words: the label, the result and the tag (the step 2 / 5
   last-sent line, the batch dialog and, in the Activity log, a mail row)
------------------------------------------------------------------------ */

/**
 * The label of an e-mail: "Approval e-mail (Approved)", or the bare name when the detail
 * (the status, the file, the organisation) is not known.
 *
 * XOL-67: an RTA's two kinds. `rta_decision` is "{status} decision e-mail" (bare: "Decision e-mail").
 * `rta_notice` is named by the activity kind that triggered it, `detail.event`, plus the option
 * number: "Option added notice (option 2)", "New RTA notice"; no event, or one this app does not
 * know, is "RTA notice", and an option event with no number is its bare name.
 *
 * @param {String} kind   approval_notice | rta_decision | rta_notice | upload_notice | file_approved | test | other
 * @param {Object} detail { status, file, org, event (rta_notice), optionNo (rta_notice) }
 */
export function mailLabel (kind, detail) {
  detail = detail || {}

  if (kind === 'rta_notice') {
    return rtaNoticeLabel(detail)
  }

  var map = {
    approval_notice: { key: 'requestMail.label.approval_notice', value: detail.status, param: 'status' },
    rta_decision: { key: 'requestMail.label.rta_decision', value: detail.status, param: 'status' },
    upload_notice: { key: 'requestMail.label.upload_notice', value: detail.file, param: 'file' },
    file_approved: { key: 'requestMail.label.file_approved', value: detail.file, param: 'file' },
    test: { key: 'requestMail.label.test', value: detail.org, param: 'org' }
  }

  if (!Object.prototype.hasOwnProperty.call(map, kind)) {
    return activityText('requestMail.label.other')
  }

  var m = map[kind]

  if (!m.value) {
    return activityText(m.key + '.bare')
  }

  var params = {}
  params[m.param] = m.value

  return activityText(m.key, params)
}

// The label of an rta_notice: by event, with the option number where the event has one.
function rtaNoticeLabel (detail) {
  var key = 'requestMail.label.rta_notice.' + detail.event

  if (!present(detail.event) || !has(COPY, key)) {
    return activityText('requestMail.label.rta_notice.bare')
  }

  // The proposal e-mail is worded by who it went to (API-3): the client gets every option in detail, the Tower Co a short note.
  if (detail.event === 'options_proposed') {
    return detail.toParty === 'owner'
      ? activityText(key + '.detailed')
      : (detail.toParty === 'towerco' ? activityText(key + '.note') : activityText(key))
  }

  if (typeof COPY[key] !== 'function') {
    return activityText(key)
  }

  return present(detail.optionNo) ? activityText(key, { n: detail.optionNo }) : activityText(key + '.bare')
}

// " — invalid address": only when every failure shares one code (`failed_codes`).
function reasonSuffix (failedCodes) {
  var codes = Object.keys(failedCodes || {}).filter(function (c) { return num(failedCodes[c]) > 0 })

  if (codes.length !== 1) {
    return ''
  }

  var key = 'requestMail.result.reason.' + codes[0]

  return Object.prototype.hasOwnProperty.call(COPY, key) ? ' — ' + activityText(key) : ''
}

// 'requestMail.result.sent' | 'resent' | 'sentSome' | 'resentSome', or the "selected" form of
// each when the batch went to selected recipients only.
function resultKey (repeat, subset, some) {
  return 'requestMail.result.' + (repeat ? 're' : '') + 'sent' + (subset ? 'Selected' : '') + (some ? 'Some' : '')
}

// The icon each result tag carries (Delta 4): a status is never colour alone.
var MAIL_TAG_ICONS = {
  sent: 'fa-check',
  sending: 'fa-clock',
  partial: 'fa-exclamation-triangle',
  failed: 'fa-times-circle',
  notSent: 'fa-minus-circle'
}

// D31 (XOL-626): the API's batch `outcome` -> the tag it shows. Sending keeps the existing
// look (no type); Not sent is `info` (grey), as for a mail that was never attempted.
var MAIL_OUTCOME_TAGS = {
  sending: { key: 'sending', type: '' },
  sent: { key: 'sent', type: 'success' },
  partly_failed: { key: 'partial', type: 'warning' },
  failed: { key: 'failed', type: 'danger' },
  not_sent: { key: 'notSent', type: 'info' }
}

/**
 * The result of one batch in words and the tag that goes with it (UX spec "Mail one-line
 * summary").
 *
 * @param {Object} m   { counts: {queued, sent, failed, skipped}, repeat, subset, failed_codes,
 *                     status: 'not_sent' (an automatic mail that was not sent), reason,
 *                     outcome: the API's 'sending' | 'sent' | 'partly_failed' | 'failed' | 'not_sent' }
 *                     The tag follows `outcome` when it is one of those five; without it (an API that
 *                     does not send it yet, or a value this app does not know) the tag is derived from
 *                     the counts, as before. The sentence always comes from the counts.
 * @param {Object} ctx { rtx }
 * @return {Object} { text: 'sent to 4 of 5 recipients · 1 failed — invalid address',
 *                    tag: { key: 'partial', text: 'Partly failed', type: 'warning', icon: 'fa-exclamation-triangle' },
 *                    total: n, counts: { queued, sent, failed, skipped } }
 */
export function mailResult (m, ctx) {
  m = m || {}
  ctx = ctx || {}

  var c = m.counts || {}
  var q = num(c.queued)
  var s = num(c.sent)
  var f = num(c.failed)
  var k = num(c.skipped)
  var n = q + s + f
  var repeat = !!m.repeat
  // Delta 1 (item 4): a batch sent to selected recipients only (a Re-send from the picker).
  var subset = !!m.subset
  var reason = reasonSuffix(m.failed_codes)
  var tag = function (key, type) {
    return { key: key, text: activityText('requestMail.tag.' + key), type: type, icon: MAIL_TAG_ICONS[key] }
  }
  var out = { total: n, counts: { queued: q, sent: s, failed: f, skipped: k } }
  // The API's own verdict, when it gave a known one.
  var fromApi = Object.prototype.hasOwnProperty.call(MAIL_OUTCOME_TAGS, m.outcome) ? MAIL_OUTCOME_TAGS[m.outcome] : null
  var done = function () {
    if (fromApi) {
      out.tag = tag(fromApi.key, fromApi.type)
    }

    return out
  }

  if (m.status === 'not_sent' && m.reason === 'mail_off') {
    out.text = activityText('requestMail.result.mailOff', { rtx: ctx.rtx })
    out.tag = tag('notSent', 'info')
    return done()
  }

  if (m.status === 'not_sent' || n === 0) {
    out.text = activityText('requestMail.result.noRecipients')
    out.tag = tag('notSent', 'info')
    return done()
  }

  var parts = []

  if (q > 0) {
    parts.push(activityText(subset ? 'requestMail.result.sendingSelected' : 'requestMail.result.sending', { n: n }))
    if (s > 0) parts.push(activityText('requestMail.result.sentSoFar', { s: s }))
    if (f > 0) parts.push(activityText('requestMail.result.failed', { f: f }) + reason)
    out.tag = tag('sending', '')
  } else if (f === n) {
    parts.push(n === 1
      ? activityText('requestMail.result.oneFailed') + reason
      : activityText('requestMail.result.allFailed', { n: n }) + reason)
    out.tag = tag('failed', 'danger')
  } else if (f > 0) {
    parts.push(activityText(resultKey(repeat, subset, true), { s: s, n: n }))
    parts.push(activityText('requestMail.result.failed', { f: f }) + reason)
    out.tag = tag('partial', 'warning')
  } else {
    parts.push(activityText(resultKey(repeat, subset, false), { n: n }))
    out.tag = tag('sent', 'success')
  }

  out.text = parts.join(' · ')

  return done()
}

/**
 * The step 2 / 5 last-sent line, from the step row's `approvalMail`
 * ({batch_id, sent_at, status, triggered_by, counts, subset, outcome, settled_at, failed_codes}).
 *
 * @param {Object} mail  step.approvalMail
 * @param {Object} ctx   { formatDateTime: function (sent_at) -> text, rtx }
 * @return {Object} { tag, text, aria, datetime, name, total }
 */
export function approvalMailLine (mail, ctx) {
  mail = mail || {}
  ctx = ctx || {}

  // A subset only ever comes from a Re-send (Delta 1, item 4), so it reads "re-sent to N
  // selected recipients" whatever `repeat` says.
  var subset = !!mail.subset
  var result = mailResult({ counts: mail.counts, failed_codes: mail.failed_codes, outcome: mail.outcome, subset: subset, repeat: subset }, ctx)
  var datetime = typeof ctx.formatDateTime === 'function' ? ctx.formatDateTime(mail.sent_at) : String(mail.sent_at || '')
  var status = mail.status || 'Approval'
  var name = mail.triggered_by && mail.triggered_by !== 'system' ? mail.triggered_by : null

  // The tag already says "Sending": the text drops its own verb ("Sending Approved e-mail to 3 recipients", not "... e-mail sending to 3").
  var resultText = result.tag && result.tag.key === 'sending' ? String(result.text).replace(/^sending /, '') : result.text

  return {
    tag: result.tag,
    text: activityText('requestMail.step.lastSent', { status: status, result: resultText, datetime: datetime, name: name }),
    aria: activityText('requestMail.step.lastSent.aria', { status: status, datetime: datetime }),
    details: activityText('requestMail.step.lastSent.details'),
    datetime: datetime,
    name: name,
    total: result.total
  }
}

/**
 * "Re-send" the e-mail the status names, or "Send" it.
 */
export function sendLabel (status, resend) {
  return activityText(resend ? 'requestMail.step.resend' : 'requestMail.step.send', { status: status })
}

/* ------------------------------------------------------------------------
   The Re-send recipient picker (Delta 1, item 4): GET /actions/{id}/notify/preview
------------------------------------------------------------------------ */

// Lowest first: Group A (failed, new), Group B (received, sending), Group C (invalid).
var RESEND_ORDER = { failed: 0, new: 1, received: 2, sending: 3, invalid: 4 }
var RESEND_TAG_TYPE = { new: 'warning', failed: 'danger', received: 'success', sending: '', invalid: 'danger' }
// The tag's icon (a status is never colour alone, design-system §9).
var RESEND_TAG_ICON = { new: 'fa-exclamation-circle', failed: 'fa-times-circle', received: 'fa-check', sending: 'fa-clock', invalid: 'fa-ban' }

/**
 * D31 (XOL-626): the `error_code`s of a failure that was the QUEUE's fault, not the address's:
 * the send job never started, was lost between retries, stopped on a non-mail-server error, or
 * the e-mail could not be queued. Re-send pre-ticks a failed address only for one of these.
 * `invalid_address`, `transport`, `unmapped_kind`, an unknown code and a missing code are listed,
 * never pre-ticked.
 */
export var QUEUE_FAILURE_CODES = ['not_run', 'job_lost', 'job_failed', 'dispatch_failed']

// Of those, the ones where nothing was attempted (the Re-send line says "Not sent", not "Last attempt failed").
var NO_ATTEMPT_CODES = ['not_run', 'dispatch_failed']

// Is this failed address one the user should not have to think about re-sending?
function isQueueFailure (code) {
  return typeof code === 'string' && QUEUE_FAILURE_CODES.indexOf(code) !== -1
}

// What this version of the e-mail did for one address. Precedence: an address that can't be
// mailed is `invalid` whatever happened before; then what the ledger says.
function resendState (r) {
  if (r.valid === false) {
    return 'invalid'
  }
  if (r.received_this_version) {
    return 'received'
  }
  if (r.last_status === 'queued') {
    return 'sending'
  }
  if (r.last_status === 'failed') {
    return 'failed'
  }
  return 'new'
}

/**
 * The picker's rows, in display order, from the preview body. Each:
 * { key, email, state: 'new' | 'failed' | 'received' | 'sending' | 'invalid',
 *   group: 'pending' | 'done' | 'invalid', selectable, selected (the default),
 *   contactName, via, lastSentAt, lastErrorCode }.
 */
export function resendRows (preview) {
  var list = preview && Array.isArray(preview.recipients) ? preview.recipients : []

  return list.map(function (r, i) {
    var state = resendState(r)
    var lastErrorCode = r.last_error_code || null

    return {
      key: r.key,
      email: r.email || r.key,
      state: state,
      group: state === 'invalid' ? 'invalid' : (state === 'new' || state === 'failed' ? 'pending' : 'done'),
      // An address that can't be mailed is listed with its reason and can't be ticked.
      selectable: state !== 'invalid',
      // New and corrected addresses are ticked, and so are ones whose last attempt failed
      // because of the queue (QUEUE_FAILURE_CODES). A failure of the address or of the mail
      // server, a received or still-sending address and an invalid one are listed, not ticked.
      selected: state === 'new' || (state === 'failed' && isQueueFailure(lastErrorCode)),
      contactName: r.contact && r.contact.name ? r.contact.name : null,
      via: r.via || null,
      lastSentAt: r.last_sent_at || null,
      lastErrorCode: lastErrorCode,
      index: i
    }
  }).sort(function (a, b) {
    var byState = RESEND_ORDER[a.state] - RESEND_ORDER[b.state]
    if (byState !== 0) return byState

    var ka = String(a.key).toLowerCase()
    var kb = String(b.key).toLowerCase()
    if (ka !== kb) return ka < kb ? -1 : 1

    return a.index - b.index
  })
}

/**
 * The keys ticked when the picker opens on this preview.
 */
export function defaultResendSelection (preview) {
  return resendRows(preview).filter(function (r) { return r.selected }).map(function (r) { return r.key })
}

/**
 * The tag of a row: { text, type, icon }.
 */
export function resendTag (state) {
  return { text: activityText('requestMail.resend.tag.' + state), type: RESEND_TAG_TYPE[state] || '', icon: RESEND_TAG_ICON[state] || '' }
}

/**
 * Line 2 of a row: the contact, "Added for this request", and the state's detail, joined with
 * " · ".
 *
 * @param {Object} row  a resendRows() row
 * @param {Object} ctx  { formatDateTime: function (text) -> text }
 */
export function resendMeta (row, ctx) {
  ctx = ctx || {}

  var parts = []
  var reasonKey = 'requestMail.result.reason.' + row.lastErrorCode
  var detail

  if (row.contactName) {
    parts.push(row.contactName)
  }
  if (row.via === 'additional') {
    parts.push(activityText('requestMail.detail.viaAdditional'))
  }

  if (row.state === 'received') {
    detail = activityText('requestMail.resend.detail.received', {
      datetime: row.lastSentAt && typeof ctx.formatDateTime === 'function' ? ctx.formatDateTime(row.lastSentAt) : ''
    })
  } else if (row.state === 'failed') {
    detail = activityText(NO_ATTEMPT_CODES.indexOf(row.lastErrorCode) !== -1 ? 'requestMail.resend.detail.notSent' : 'requestMail.resend.detail.failed', {
      reason: row.lastErrorCode && Object.prototype.hasOwnProperty.call(COPY, reasonKey) ? activityText(reasonKey) : null
    })
  } else {
    detail = activityText('requestMail.resend.detail.' + row.state)
  }
  parts.push(detail)

  return parts.join(' · ')
}

/**
 * The warning after a 409 `preview_stale`: { title, body }. `stale` is { reason:
 * 'content_changed' | 'recipients_changed', unknown: [addresses the server no longer has] }.
 */
export function resendStaleText (stale) {
  stale = stale || {}

  if (stale.reason === 'recipients_changed') {
    var unknown = Array.isArray(stale.unknown) ? stale.unknown : []

    return {
      title: activityText('requestMail.resend.stale.recipients.title'),
      body: activityText('requestMail.resend.stale.recipients.body', {
        emails: emailsText(unknown.map(function (e) { return { email: e } })),
        n: unknown.length
      })
    }
  }

  return {
    title: activityText('requestMail.resend.stale.content.title'),
    body: activityText('requestMail.resend.stale.content.body')
  }
}

/**
 * How a batch was triggered, in words (the batch dialog's "Triggered" row).
 *
 * XOL-67 (v2 UX spec 4.8, API-16): an RTA's automatic e-mails are worded by the activity kind that
 * triggered them (`batch.event`): the proposal is "Sent when the options were proposed", a Step 3
 * Decline "Sent when the option was declined", a review submitted with Submit review "Sent when the
 * review was submitted"; the completion and rejection notices keep "Automatically, when {name}
 * completed / rejected the RTA". The decision e-mail (`rta_decision`) is the existing "Sent by {name}" /
 * "Re-sent by {name}". Anything it cannot word (no event, no name) is the bare "Automatically".
 *
 * @param {Object} batch { kind, trigger, triggered_by, repeat, file, event, option_no, option_count }
 *                       (API DEV-32 as API-16 changes it: on every batch, null unless an RTA's;
 *                       `option_no` is null on a proposal; `from_review` is gone. `option_count` is on the mail ACTIVITY row's
 *                       details, not on the batch, P-S10 as corrected: activityRow reads it as `optionCount`)
 * @param {Object} [opts] { short: true } leaves the file name out of an automatic mail's line
 *                        (the dialog header already shows it)
 */
export function mailTriggerText (batch, opts) {
  batch = batch || {}
  opts = opts || {}

  var name = batch.triggered_by && batch.triggered_by !== 'system' ? batch.triggered_by : null
  var file = batch.file && typeof batch.file === 'object' ? batch.file.name : batch.file
  var optionNo = present(batch.option_no) ? batch.option_no : null

  if (batch.kind === 'rta_notice') {
    var rtaKey = 'requestMail.batch.trigger.' + (batch.trigger === 'auto' ? 'auto' : 'button') + '.rta_notice'

    if (batch.trigger === 'auto') {
      rtaKey += '.' + batch.event

      if (has(COPY, rtaKey)) {
        // v2: the proposal and the Step 3 Decline are worded without a name; the completion and rejection lines name who did it.
        if (typeof COPY[rtaKey] !== 'function') {
          return activityText(rtaKey)
        }

        if (name) {
          return activityText(rtaKey, { name: name, n: optionNo })
        }
      }

      return activityText('requestMail.batch.trigger.auto.system')
    }

    // A review submitted with Submit review: the one button that sends an rta_notice.
    if (batch.trigger === 'button' && batch.event === 'option_reviewed') {
      return activityText(rtaKey + '.option_reviewed')
    }
  }

  if (batch.trigger === 'auto') {
    var key = 'requestMail.batch.trigger.auto.' + batch.kind

    if (name && file && opts.short && Object.prototype.hasOwnProperty.call(COPY, key + '.short')) {
      return activityText(key + '.short', { name: name })
    }
    if (name && file && Object.prototype.hasOwnProperty.call(COPY, key)) {
      return activityText(key, { name: name, file: file })
    }
    return activityText('requestMail.batch.trigger.auto.system')
  }

  var who = name || 'the system'

  if (batch.trigger === 'legacy') {
    return activityText('requestMail.batch.trigger.legacy', { name: who })
  }
  if (batch.kind === 'test') {
    return activityText('requestMail.batch.trigger.test', { name: who })
  }
  return activityText(batch.repeat ? 'requestMail.batch.trigger.repeat' : 'requestMail.batch.trigger.button', { name: who })
}

/**
 * Show a toast built by notifyOutcome / noticeOutcome on a component: a plain
 * `$message`, or a `$notify` with one paragraph per sentence. Nothing for a null toast.
 */
export function showToast (vm, toast) {
  if (!toast) {
    return
  }

  if (toast.channel === 'message') {
    vm.$message({ message: toast.message, type: toast.type })
    return
  }

  var message = toast.message

  if (toast.lines && toast.lines.length > 1) {
    var h = vm.$createElement

    message = h('div', toast.lines.map(function (line) {
      return h('p', { style: 'margin: 2px 0;' }, line)
    }))
  }

  vm.$notify({ title: toast.title, message: message, type: toast.type, duration: toast.duration })
}

/**
 * HTML-escape a text that goes into a message the app renders as HTML (an organisation's
 * name inside a `dangerouslyUseHTMLString` confirm).
 */
export function escapeHtml (value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/* ------------------------------------------------------------------------
   The request Activity log (XOL-586 NEW-B): GET /actions/{id}/activity
------------------------------------------------------------------------ */

export var ACTIVITY_PAGE_SIZE = 25

// The API's `type` groups, in the order the Type select lists them.
var TYPE_GROUPS = ['mail', 'status', 'fields', 'files']

function extend (target) {
  for (var i = 1; i < arguments.length; i++) {
    var src = arguments[i]
    if (src) {
      Object.keys(src).forEach(function (k) { target[k] = src[k] })
    }
  }
  return target
}

function present (v) {
  return v !== undefined && v !== null && v !== ''
}

function orgShort (o) {
  return o ? (o.shortName || o.name || null) : null
}

// The text of a copy key, or '' for a key this table does not have.
function textOr (key, params, fallback) {
  return Object.prototype.hasOwnProperty.call(COPY, key) ? activityText(key, params) : fallback
}

/**
 * The Type select: [{ value, label }], "All types" first. `value` is the API's `type`.
 */
export function activityTypeOptions () {
  return [{ value: '', label: activityText('requestActivity.filter.type.all') }].concat(
    TYPE_GROUPS.map(function (g) {
      return { value: g, label: activityText('requestActivity.filter.type.' + g) }
    })
  )
}

/**
 * The Step select: "All steps", then one "Step {n}: {title}" per Tower Co (party 1)
 * step of the request, by number. `value` is the step number the API's `step` filters on.
 *
 * @param {Array} steps the request's steps ({ stepNo, partyNo, stepName })
 */
export function activityStepOptions (steps) {
  var seen = {}
  var options = []

  ;(steps || []).forEach(function (s) {
    var n = Number(s && s.stepNo)

    if (!s || Number(s.partyNo) !== 1 || !(n > 0) || seen[n]) {
      return
    }

    seen[n] = true
    options.push({ value: n, label: activityText('requestActivity.filter.step.option', { n: n, title: s.stepName || null }) })
  })

  options.sort(function (a, b) { return a.value - b.value })

  return [{ value: '', label: activityText('requestActivity.filter.step.all') }].concat(options)
}

/**
 * The query string of one page: `page=2&per_page=25&step=2&type=mail`. A filter that is
 * empty, or not one the API knows, is left out, so the request never carries a made-up
 * value. `per_page` is capped at 100, the API's maximum.
 *
 * @param {Object} q { page, perPage, step, type } (`type` is one group or an array of them)
 */
export function activityQuery (q) {
  q = q || {}

  var page = parseInt(q.page, 10)
  var per = parseInt(q.perPage, 10)
  var parts = [
    'page=' + (page > 0 ? page : 1),
    'per_page=' + (per > 0 ? Math.min(per, 100) : ACTIVITY_PAGE_SIZE)
  ]
  var step = Number(q.step)

  if (present(q.step) && step > 0) {
    parts.push('step=' + step)
  }

  var types = (Array.isArray(q.type) ? q.type : String(present(q.type) ? q.type : '').split(','))
    .map(function (t) { return String(t).trim() })
    .filter(function (t) { return TYPE_GROUPS.indexOf(t) !== -1 })

  if (types.length > 0) {
    parts.push('type=' + encodeURIComponent(types.join(',')))
  }

  return parts.join('&')
}

/** True when a step or a type filter is set. */
export function activityIsFiltered (f) {
  f = f || {}
  return (present(f.step) && Number(f.step) > 0) || (present(f.type) && String(f.type) !== '')
}

/**
 * The paginator body as the list uses it. Reads Laravel's flat paginator, or one nested
 * under `meta`; a body without paging facts is one page of whatever came.
 *
 * @return {Object} { rows, total, page, perPage, lastPage }
 */
export function parseActivityPage (body, fallback) {
  fallback = fallback || {}

  var rows = body && Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : [])
  var meta = body && !Array.isArray(body) && body.meta && typeof body.meta === 'object' ? body.meta : (body || {})
  var perPage = num(meta.per_page) || fallback.perPage || ACTIVITY_PAGE_SIZE
  var total = present(meta.total) ? num(meta.total) : rows.length

  return {
    rows: rows,
    total: total,
    page: num(meta.current_page) || fallback.page || 1,
    perPage: perPage,
    lastPage: num(meta.last_page) || Math.max(1, Math.ceil(total / perPage))
  }
}

/**
 * "Showing 1 to 25 of 61 entries." for the hidden live region, or '' with nothing shown.
 */
export function activityRangeText (info) {
  if (!info || !info.rows || info.rows.length === 0) {
    return ''
  }

  var from = (info.page - 1) * info.perPage + 1

  return activityText('requestActivity.status.range', { from: from, to: from + info.rows.length - 1, total: info.total })
}

/**
 * The "log started after this request was created" footnote: only on the last page of the
 * UNFILTERED log, and only when the oldest row shown is not the request's creation. A
 * filtered list would claim it about rows it simply left out.
 */
export function activityShowsStartNote (info, filtered) {
  if (filtered || !info || !info.rows || info.rows.length === 0 || info.page < info.lastPage) {
    return false
  }

  return info.rows[info.rows.length - 1].kind !== 'request_created'
}

// The organisation a party is called in a sentence: its short name, else the party word.
function partyOrg (party, ctx) {
  var orgs = ctx && ctx.orgs ? ctx.orgs : {}

  return orgs[party] || textOr('requestMail.party.' + party, null, party || '')
}

function whoOf (row) {
  var a = row.actor

  if (!a || typeof a !== 'object') {
    return { system: false, name: null, org: null, text: activityText('requestActivity.actor.unknown') }
  }

  if (a.party === 'system' || !a.name) {
    return { system: true, name: null, org: null, text: activityText('requestActivity.actor.system') }
  }

  var org = orgShort(a.organisation)

  return {
    system: false,
    name: a.name,
    org: org,
    text: org ? activityText('requestActivity.actor.withOrg', { name: a.name, org: org }) : a.name
  }
}

// The option an RTA row is about: business rows carry `optionNo`, mail rows `option_no` (API DEV-6).
function optionNoOf (details) {
  if (!details) return null
  if (present(details.optionNo)) return details.optionNo
  return present(details.option_no) ? details.option_no : null
}

// XOL-67: an RTA row's meta line is "Step 3 · Option 2" (UX v4 section 3), the option whenever the
// row has one; `title` is the RTA's own name for the step ("Client Review"). Another request's
// rows are exactly as they were.
function stepOf (row, ctx) {
  var rta = !!ctx && ctx.rtx === 'RTA'
  var option = rta ? optionNoOf(row.details) : null
  var suffix = option ? ' · Option ' + option : ''

  if (!present(row.step_no)) {
    return { text: activityText('requestActivity.step.request') + suffix, request: true }
  }

  if (Number(row.party_no) === 2) {
    return { text: activityText('requestActivity.step.chipParty', { n: row.step_no, party: partyOrg('owner', ctx) }), request: false }
  }

  var chip = { text: activityText('requestActivity.step.chip', { n: row.step_no }) + suffix, request: false }

  if (rta && rtaStepName(row.step_no)) {
    chip.title = rtaStepName(row.step_no)
  }

  return chip
}

function isPendingStatus (v) {
  return !present(v) || String(v).toLowerCase() === 'pending'
}

function fileName (row, details) {
  return details.name || (row.file && row.file.name) || '(unnamed file)'
}

// The labels of the paths a field edit touched, de-duplicated, in first-seen order.
function pathLabels (row, details, ctx) {
  var kind = details.table === 'action_steps' ? 'step' : 'action'
  var labelCtx = extend({}, ctx && ctx.labelCtx, { partyNo: row.party_no, stepNo: row.step_no })
  var out = []

  ;(Array.isArray(details.paths) ? details.paths : []).forEach(function (p) {
    var label = fieldLabel(kind, p, labelCtx)

    if (out.indexOf(label) === -1) {
      out.push(label)
    }
  })

  if (out.length === 0) {
    out.push(fieldLabel(kind, null, labelCtx))
  }

  return out
}

function fieldsText (row, details, ctx) {
  var labels = pathLabels(row, details, ctx)

  if (labels.length === 1) return activityText('requestActivity.kind.fields_changed.one', { a: labels[0] })
  if (labels.length === 2) return activityText('requestActivity.kind.fields_changed.two', { a: labels[0], b: labels[1] })
  if (labels.length === 3) return activityText('requestActivity.kind.fields_changed.three', { a: labels[0], b: labels[1], c: labels[2] })

  return activityText('requestActivity.kind.fields_changed.many', { a: labels[0], b: labels[1], n: labels.length - 2 })
}

// XOL-626 D30: the API's kinds for the request-scoped sharer writes (xolas-tms-api #285), the ONLY
// place their names are spelled. A rename on the API side is a one-line edit here.
var SHARER_KIND = {
  created: 'sharer_created',
  updated: 'sharer_updated',
  shared: 'rate_shared',
  unshared: 'rate_unshared',
  linked: 'sharer_linked',
  finance: 'sharer_finance_changed'
}

// The column names in details.fields -> the step-3 form labels, exactly as LicenseeForm.vue / FinanceSharer.vue print them. A column not listed reads "Other details".
var SHARER_FIELD_LABELS = {
  telcoSiteId: 'Telco Site ID',
  serviceType: 'Service Type',
  spectrumBand: 'Spectrum',
  onAir: 'On Air Date',
  capex: 'CAPEX',
  opex: 'OPEX',
  handover: 'Handover Date',
  started: 'Agreement Start',
  startBilling: 'Billing Start',
  expiry: 'Expiry Date',
  sharerNo: 'Sharer No.',
  rate_id: 'Sharer Rate',
  discount: 'Discount',
  discountPercentage: 'Disc. Type',
  interestRate: 'Interest Rate'
}

// details.fields as labels, de-duplicated, in first-seen order.
function sharerFieldLabels (details) {
  var out = []

  ;(Array.isArray(details.fields) ? details.fields : []).forEach(function (f) {
    var label = Object.prototype.hasOwnProperty.call(SHARER_FIELD_LABELS, f) ? SHARER_FIELD_LABELS[f] : 'Other details'

    if (out.indexOf(label) === -1) {
      out.push(label)
    }
  })

  return out
}

// The field list worded like fields_changed: one, "a and b", "a, b and c", "a, b and n other fields".
function sharerListText (labels) {
  var base = 'requestActivity.kind.sharer.list.'

  if (labels.length === 0) return 'Other details'
  if (labels.length === 1) return activityText(base + 'one', { a: labels[0] })
  if (labels.length === 2) return activityText(base + 'two', { a: labels[0], b: labels[1] })
  if (labels.length === 3) return activityText(base + 'three', { a: labels[0], b: labels[1], c: labels[2] })

  return activityText(base + 'many', { a: labels[0], b: labels[1], n: labels.length - 2 })
}

// The sharer's number, or null when the API sent none (null, 0, ''): "Created the sharer".
function sharerNoOf (details) {
  var n = Number(details.sharerNo)

  return n > 0 ? n : null
}

// "Changed the sharer's {list}" / "Changed {list}": the sentence in parts, the list as the bold object.
function sharerNamed (labels) {
  var list = sharerListText(labels)
  var text = activityText('requestActivity.kind.sharer.named', { list: list })

  return { text: text, parts: [part(text.slice(0, text.length - list.length), 'lead'), part(list, 'obj')] }
}

function isSharerKind (k) {
  return Object.keys(SHARER_KIND).some(function (name) { return SHARER_KIND[name] === k })
}

// -> { text, parts } for the six sharer kinds; the sentence in parts, its object in bold.
function sharerWhat (k, details, rtx) {
  var no = sharerNoOf(details)
  var text

  switch (k) {
    case SHARER_KIND.created:
      text = activityText('requestActivity.kind.sharer.created', { no: no })
      return { text: text, parts: no ? [part('Created sharer ', 'lead'), part('#' + no, 'obj')] : null }
    case SHARER_KIND.updated:
      return sharerNamed(sharerFieldLabels(details))
    case SHARER_KIND.shared:
      return { text: activityText('requestActivity.kind.sharer.shared'), parts: null }
    case SHARER_KIND.unshared:
      return { text: activityText('requestActivity.kind.sharer.unshared'), parts: null }
    case SHARER_KIND.linked:
      text = activityText('requestActivity.kind.sharer.linked', { no: no, rtx: rtx, reset: details.share_reset === true })
      return { text: text, parts: null }
  }

  // SHARER_KIND.finance: `what` says which write it was; named columns are listed.
  var what = details.what
  var labels = sharerFieldLabels(details)

  if (what === 'rate_override_set' || what === 'rate_override_cleared' || what === 'rate_version') {
    return { text: activityText('requestActivity.kind.sharer.finance.' + what), parts: null }
  }

  if (labels.length > 0) {
    return sharerNamed(labels)
  }

  return { text: activityText('requestActivity.kind.sharer.finance.fields'), parts: null }
}

function recipientsText (details, ctx) {
  var list = details.list === 'unsubscribed' ? 'unsubscribed' : 'additional'
  var org = partyOrg(details.party, ctx)
  var base = 'requestActivity.kind.recipients_changed.' + list + '.'
  var counts = { added: num(details.added), removed: num(details.removed), changed: num(details.changed) }
  var set = ['added', 'removed', 'changed'].filter(function (k) { return counts[k] > 0 })

  if (set.length === 0) {
    return activityText('requestActivity.kind.recipients_changed.noNet', {
      org: org,
      list: activityText('requestActivity.kind.recipients_changed.list.' + list)
    })
  }

  if (set.length === 1) {
    return activityText(base + set[0], { n: counts[set[0]], org: org })
  }

  return activityText(base + 'mixed', {
    org: org,
    parts: set.map(function (k) { return activityText(base + 'part.' + k, { n: counts[k] }) }).join(' · ')
  })
}

function approvalDecidedText (details, ctx) {
  var from = details.from
  var to = details.to
  var text

  if (typeof to === 'boolean') {
    text = activityText(to ? 'requestActivity.kind.approval_decided.approvedBool' : 'requestActivity.kind.approval_decided.removedBool')
  } else if (isPendingStatus(to)) {
    text = activityText('requestActivity.kind.approval_decided.reset')
  } else if (isPendingStatus(from)) {
    text = activityText('requestActivity.kind.approval_decided.set', { to: to })
  } else if (String(from).toLowerCase() === String(to).toLowerCase()) {
    // Only the decision date moved: the API records { from: 'Approved', to: 'Approved', date }.
    text = activityText('requestActivity.kind.approval_decided.dateOnly')
  } else {
    text = activityText('requestActivity.kind.approval_decided.changed', { from: from, to: to })
  }

  if (present(details.date)) {
    var date = ctx && typeof ctx.formatDate === 'function' ? ctx.formatDate(details.date) : String(details.date)

    text += activityText('requestActivity.kind.approval_decided.dated', { date: date })
  }

  return text
}

// A mail row: the label and the result, the tag, and whether it opens the batch dialog.
function mailWhat (row, ctx) {
  var mail = row.mail && typeof row.mail === 'object' ? row.mail : {}
  var details = row.details || {}
  var kind = mail.kind || details.kind
  var fileOf = row.file && row.file.name ? row.file.name : null
  var org = present(details.party) ? partyOrg(details.party, ctx) : null
  var status = details.approval_status || null
  // XOL-67: an rta_notice is named by its event and option (mail rows carry `option_no`).
  var rtaDetail = { event: details.event, optionNo: optionNoOf(details), toParty: details.to_party || details.toParty || details.party || null }
  var label = mailLabel(kind, extend({ status: status, file: fileOf, org: org }, rtaDetail))
  var bare = mailLabel(kind, extend({ status: status, file: null, org: org }, rtaDetail))
  var result = mailResult({
    counts: mail.counts,
    repeat: !!(mail.repeat || details.repeat),
    subset: !!(mail.subset || details.subset),
    failed_codes: mail.failed_codes,
    outcome: mail.outcome,
    status: details.status,
    reason: details.reason
  }, { rtx: ctx.rtx })
  var parts

  // "Upload notice (TP_rev2.pdf)": the label, the file in its own part (so it can be shortened).
  if (fileOf && label !== bare && label === bare + ' (' + fileOf + ')') {
    parts = [part(bare, 'obj'), part(' ', 'plain'), part('(' + fileOf + ')', 'file', fileOf)]
  } else {
    parts = [part(label, 'obj')]
  }

  parts.push(part(' ' + result.text, 'tail'))

  return {
    text: label + ' ' + result.text,
    parts: parts,
    tag: { text: result.tag.text, type: result.tag.type, icon: result.tag.icon },
    dialog: present(mail.batch_id) ? 'mail' : null
  }
}

/* ------------------------------------------------------------------------
   Delta 4: how a row looks. The icon and tone of each kind, the sentence in parts
   (so the object and the file name can be set in bold), file names shortened in the
   middle, and the rows grouped by day.
------------------------------------------------------------------------ */

// kind -> [icon, tone]. A tone is `info` (grey), `success` or `danger`: colour is only for
// a real outcome (D2). approval_decided is refined by its value in activityKindLook.
var KIND_LOOK = {
  mail: ['fa-envelope', 'info'],
  request_created: ['fa-plus-circle', 'info'],
  fields_changed: ['fa-edit', 'info'],
  recipients_changed: ['fa-address-book', 'info'],
  mail_turned_on: ['fa-bell', 'info'],
  mail_turned_off: ['fa-bell-slash', 'info'],
  approval_decided: ['fa-stamp', 'info'],
  approval_locked: ['fa-lock', 'info'],
  approval_unlocked: ['fa-lock-open', 'info'],
  step_completed: ['fa-check', 'info'],
  step_reopened: ['fa-undo', 'info'],
  request_completed: ['fa-flag', 'success'],
  request_rejected: ['fa-ban', 'danger'],
  category_added: ['fa-folder-plus', 'info'],
  category_removed: ['fa-folder-minus', 'info'],
  file_uploaded: ['fa-upload', 'info'],
  file_replaced: ['fa-exchange-alt', 'info'],
  file_removed: ['fa-trash-alt', 'info'],
  file_approved: ['fa-check-circle', 'success'],
  file_unapproved: ['fa-times-circle', 'info'],
  file_renamed: ['fa-pen', 'info'],
  file_details_changed: ['fa-file-alt', 'info'],
  file_first_download: ['fa-download', 'info'],
  file_downloaded: ['fa-download', 'info'],
  other: ['fa-ellipsis-h', 'info']
}

// XOL-67: the RTA kinds (v2 UX spec 4.8, API-15). Colour is for an outcome only (D2): a review (option_reviewed) is refined by its value in
// activityKindLook. The v1 kinds option_client_proposed, option_proposed, option_withdrawn, option_client_withdrawn, option_client_responded,
// rta_decision_locked and rta_nominal_updated are gone with their endpoints (no production row holds them) and read as "Other change"; a request or option edit is a plain `fields_changed` (API P-S29), worded by rtaWhat.
KIND_LOOK.option_added = ['fa-plus', 'info']
KIND_LOOK.option_deleted = ['fa-trash-alt', 'info']
KIND_LOOK.options_proposed = ['fa-paper-plane', 'info']
KIND_LOOK.rta_back_to_draft = ['fa-undo', 'info']
KIND_LOOK.option_reviewed = ['fa-undo', 'info']
KIND_LOOK.option_declined = ['fa-times-circle', 'danger']
KIND_LOOK.rta_towerco_decided = ['fa-handshake', 'info']
KIND_LOOK.rta_decision_reset = ['fa-undo', 'info']
KIND_LOOK.rta_decision_unlocked = ['fa-lock-open', 'info']
KIND_LOOK.rta_transitioned = ['fa-lock', 'success']
KIND_LOOK.rta_completion_edited = ['fa-edit', 'info']
KIND_LOOK.rta_site_deleted = ['fa-link-slash', 'info']

// XOL-626 D30: the sharer kinds (all `info`: an edit is not an outcome). FA 6.1.2 has every glyph.
KIND_LOOK[SHARER_KIND.created] = ['fa-plus-circle', 'info']
KIND_LOOK[SHARER_KIND.updated] = ['fa-edit', 'info']
KIND_LOOK[SHARER_KIND.shared] = ['fa-eye', 'info']
KIND_LOOK[SHARER_KIND.unshared] = ['fa-eye-slash', 'info']
KIND_LOOK[SHARER_KIND.linked] = ['fa-link', 'info']
KIND_LOOK[SHARER_KIND.finance] = ['fa-coins', 'info']

/**
 * The glyph and tone of an activity kind: { icon: 'fa-envelope', tone: 'info' }. An unknown kind
 * is `other`. An approval is `success` when it was set to Approved (or ticked), `danger` when
 * it was set to Rejected, otherwise `info`; an RTA review (option_reviewed) takes its icon and tone from the status it was submitted as.
 */
export function activityKindLook (kind, details) {
  var look = Object.prototype.hasOwnProperty.call(KIND_LOOK, kind) ? KIND_LOOK[kind] : KIND_LOOK.other
  var tone = look[1]
  var icon = look[0]

  if (kind === 'approval_decided') {
    var to = details ? details.to : null

    if (to === true || String(to).toLowerCase() === 'approved') {
      tone = 'success'
    } else if (String(to).toLowerCase() === 'rejected') {
      tone = 'danger'
    }
  }

  // UX spec 4.8: moving the requested point is a crosshairs (an option's pin move keeps the edit pencil).
  if (kind === 'fields_changed' && rtaPointMove(details) === 'request') {
    icon = 'fa-crosshairs'
  }

  // A submitted review (v2): Approved is a green thumbs-up, Declined a red cross, Pending (a reversal) a grey undo.
  if (kind === 'option_reviewed') {
    var word = responseWord(details ? details.to : null)

    icon = word === 'Approved' ? 'fa-thumbs-up' : (word === 'Declined' ? 'fa-times-circle' : 'fa-undo')
    tone = word === 'Approved' ? 'success' : (word === 'Declined' ? 'danger' : 'info')
  }

  return { icon: icon, tone: tone }
}

/**
 * A file name shortened in the middle so the extension survives: when it is longer than `max`,
 * the first `max - 16` characters, an ellipsis, then the last 15. The result is `max` long.
 */
export function shortFileName (name, max) {
  var s = name === null || name === undefined ? '' : String(name)
  var limit = Number(max) > 0 ? Number(max) : 40
  var tail = 15

  if (s.length <= limit) {
    return s
  }

  var head = limit - tail - 1

  if (head < 1) {
    return s.slice(0, Math.max(limit - 1, 1)) + '…'
  }

  return s.slice(0, head) + '…' + s.slice(s.length - tail)
}

function part (text, tone, file) {
  var p = { text: text, tone: tone }

  if (file) {
    p.file = file
  }

  return p
}

/**
 * What a sentence part reads as: its text, with the file name inside it (a `file` part) cut to
 * `max` characters in the middle. The full name stays in `part.file` for the tooltip.
 */
export function partText (p, max) {
  if (!p || !p.file) {
    return p ? p.text : ''
  }

  var at = p.text.indexOf(p.file)

  if (at < 0) {
    return p.text
  }

  return p.text.slice(0, at) + shortFileName(p.file, max) + p.text.slice(at + p.file.length)
}

// The sentence cut around ordered needles ({ str, tone, file }): the text before the first and
// between two is `lead`, the text after the last is `tail`. A needle that cannot be found
// leaves the sentence as one `plain` part (it is never lost).
function splitParts (text, needles) {
  if (!text) {
    return []
  }

  var out = []
  var cursor = 0

  for (var i = 0; i < needles.length; i++) {
    var n = needles[i]
    var at = n.str ? text.indexOf(n.str, cursor) : -1

    if (at < 0) {
      return [part(text, 'plain')]
    }

    if (at > cursor) {
      out.push(part(text.slice(cursor, at), 'lead'))
    }

    out.push(part(n.str, n.tone, n.file))
    cursor = at + n.str.length
  }

  if (cursor < text.length) {
    out.push(part(text.slice(cursor), 'tail'))
  }

  return out
}

// The approval sentence with its final status as the object: "Set the approval to **Approved**
// (dated …)". Only a set or a change names a status; the other forms stay one plain part.
function approvalParts (text, details) {
  var to = details.to
  var setOrChanged = typeof to === 'string' && !isPendingStatus(to) &&
    (isPendingStatus(details.from) || String(details.from).toLowerCase() !== to.toLowerCase())

  if (!setOrChanged) {
    return [part(text, 'plain')]
  }

  var datedAt = text.indexOf(' (dated ')
  var core = datedAt >= 0 ? text.slice(0, datedAt) : text

  if (core.length <= to.length || core.slice(core.length - to.length) !== to) {
    return [part(text, 'plain')]
  }

  var out = [part(core.slice(0, core.length - to.length), 'lead'), part(to, 'obj')]

  if (datedAt >= 0) {
    out.push(part(text.slice(datedAt), 'tail'))
  }

  return out
}

function quoted (name) {
  return '“' + name + '”'
}

function fileNeedle (name) {
  return { str: quoted(name), tone: 'file', file: name }
}

/**
 * The rows grouped by day, in the order they came (newest first): [{ key, label, sub, rows }].
 * `rows` are activityRow results (each carries its `dayKey`, the app-clock day). The group of
 * `todayKey` is "Today" and the day before it "Yesterday", each with the date as `sub`; any other
 * day is labelled with its date alone. `todayKey` is the app clock's day (appNow), never the
 * browser's (XOL-414); without one, appNow() is used.
 */
export function activityDays (rows, todayKey) {
  var today = todayKey || appNow().format('YYYY-MM-DD')
  var todayAt = toAppTime(today)
  var yesterday = todayAt ? todayAt.clone().subtract(1, 'day').format('YYYY-MM-DD') : ''
  var groups = []

  ;(rows || []).forEach(function (r) {
    var key = r && r.dayKey ? r.dayKey : ''
    var group = groups.length > 0 ? groups[groups.length - 1] : null

    if (!group || group.key !== key) {
      var at = key ? toAppTime(key) : null
      var full = at ? at.format('ddd D MMM YYYY') : ''

      group = { key: key, label: full, sub: null, rows: [] }

      if (key && key === today) {
        group.label = activityText('requestActivity.day.today')
        group.sub = full
      } else if (key && key === yesterday) {
        group.label = activityText('requestActivity.day.yesterday')
        group.sub = full
      }

      groups.push(group)
    }

    group.rows.push(r)
  })

  return groups
}

// One minute and one day on the app clock, for comparing a recipient's time with its batch's.
function minuteKey (value) {
  var at = value ? toAppTime(value) : null

  return at ? at.format('YYYY-MM-DD HH:mm') : null
}

function dayOf (value) {
  var at = value ? toAppTime(value) : null

  return at ? at.format('YYYY-MM-DD') : null
}

// A time as the recipient table reads it: "12:53 am" on the batch's own day, else the full
// date and time (ctx.formatDateTime).
function whenText (value, batchAt, ctx) {
  if (!value) {
    return ''
  }

  var day = dayOf(value)

  if (day && day === dayOf(batchAt)) {
    return toAppTime(value).format('hh:mm a')
  }

  return typeof ctx.formatDateTime === 'function' ? ctx.formatDateTime(value) : String(value)
}

var RECIPIENT_TAGS = {
  sent: { type: 'success', icon: 'fa-check' },
  queued: { type: '', icon: 'fa-clock' },
  failed: { type: 'danger', icon: 'fa-times-circle' }
}

/**
 * What the Status column of the e-mail dialog says about one address (Delta 4).
 *
 * @param {Object} r        the recipient { status, sent_at, queued_at, failed_at, attempts, error_code }
 * @param {String} batchAt  the batch's created_at
 * @param {Object} ctx      { formatDateTime }
 * @return {Object} { tag: { text, type, icon }, detail } -- `detail` is '' when it would only repeat
 *                  the batch's own time: a SENT address whose sent_at falls in the same minute as
 *                  the batch (and was not retried) says nothing but its tag.
 */
export function mailRecipientStatus (r, batchAt, ctx) {
  r = r || {}
  ctx = ctx || {}

  var look = Object.prototype.hasOwnProperty.call(RECIPIENT_TAGS, r.status) ? RECIPIENT_TAGS[r.status] : { type: '', icon: 'fa-question-circle' }
  var attempts = Number(r.attempts) || 0
  var tag = { text: textOr('requestMail.status.' + r.status, null, r.status || ''), type: look.type, icon: look.icon }
  var detail = ''

  if (r.status === 'sent') {
    var differs = !!r.sent_at && minuteKey(r.sent_at) !== minuteKey(batchAt)

    if (differs) {
      detail = activityText('requestMail.detail.sent', { datetime: whenText(r.sent_at, batchAt, ctx) })
    }
  } else if (r.status === 'queued') {
    detail = activityText('requestMail.detail.queued', { datetime: whenText(r.queued_at, batchAt, ctx) }).replace(/\s+$/, '')
  } else {
    var reason = textOr('requestMail.reason.' + r.error_code, null, activityText('requestMail.reason.unknown'))

    detail = activityText('requestMail.detail.failed', { reason: reason, datetime: r.failed_at ? whenText(r.failed_at, batchAt, ctx) : null })
  }

  if (attempts > 1) {
    var tried = r.status === 'failed'
      ? activityText('requestMail.detail.failedAttempts', { a: attempts })
      : activityText('requestMail.detail.attempts', { a: attempts })

    // A retried address always has something to say, even when its time matches the batch's.
    if (r.status === 'sent' && detail === '' && r.sent_at) {
      detail = activityText('requestMail.detail.sent', { datetime: whenText(r.sent_at, batchAt, ctx) })
    }

    detail = detail ? detail + ' · ' + tried : tried.charAt(0).toUpperCase() + tried.slice(1)
  }

  return { tag: tag, detail: detail }
}

/* ------------------------------------------------------------------------
   XOL-67: the sentence of each RTA activity kind (UX v4 section 3). "Bold is the object": a sentence
   is a list of [text, tone] pieces ('lead' | 'obj' | 'tail' | 'plain'), so the text and its
   parts are built from the same pieces and cannot drift apart.
------------------------------------------------------------------------ */

function sentence (pieces) {
  var parts = pieces.filter(function (p) { return p[0] !== '' && p[0] !== null && p[0] !== undefined })
    .map(function (p) { return part(p[0], p[1]) })

  return {
    text: parts.map(function (p) { return p.text }).join(''),
    parts: parts.length > 0 ? parts : null
  }
}

// "option 2", or "an option" for a row the API sent without one.
function optionText (n) {
  return present(n) ? 'option ' + n : 'an option'
}

// The word a review is shown as. The API's `to` / `from` is a review status (Pending, Approved, Declined; I-26) or the option status it
// produced (Review, Approved, Declined): Approved / Declined / Pending.
function responseWord (v) {
  var t = String(present(v) ? v : '').toLowerCase()

  if (t.indexOf('approved') !== -1) return 'Approved'
  if (t.indexOf('declined') !== -1) return 'Declined'

  return 'Pending'
}

// A reason or remarks, cut at 500 characters with an ellipsis (the API already sends plain text of at most 500: API-9, API-11).
function cutAt500 (text) {
  var t = String(present(text) ? text : '')

  return t.length > 500 ? t.slice(0, 500) + '…' : t
}

// "4", "4 and 5", "3, 4 and 5": numbers in a sentence.
function numbersText (list) {
  var items = list.map(String)

  return items.length <= 1 ? items.join('') : items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1]
}

// "option 3" / "options 1, 2 and 4" from the API's `optionNos`; '' when it sent none.
function optionsText (nos) {
  var list = Array.isArray(nos) ? nos.filter(present) : []

  return list.length === 0 ? '' : (list.length === 1 ? 'option ' : 'options ') + numbersText(list)
}

var COMPLETION_SECTIONS = {
  site: 'Details',
  details: 'Details',
  structure: 'Structure',
  locality: 'Locality',
  tenancy: 'Tenancy',
  finance: 'Finance',
  rts: 'RTS'
}

function completionLabels (details) {
  var out = []

  ;(Array.isArray(details.sections) ? details.sections : []).forEach(function (k) {
    var label = has(COMPLETION_SECTIONS, k) ? COMPLETION_SECTIONS[k] : String(k)

    if (out.indexOf(label) === -1) {
      out.push(label)
    }
  })

  return out
}

// The Tower Co decision of an RTA: set, changed, reset to Pending, only the date or the remarks.
// Who deleted the site, for "deleted by {name}": the row's actor when it is a person, else
// `details.by` ONLY when it is a display name (it may be an id, which is never printed).
function deletedBy (row, details) {
  var a = row && row.actor

  if (a && typeof a === 'object' && a.party !== 'system' && present(a.name)) {
    return a.name
  }

  var by = details.by

  return typeof by === 'string' && by !== '' && !/^\d+$/.test(by) ? by : null
}

function rtaDecidedWhat (details, ctx) {
  var from = details.from
  var to = details.to
  var n = optionNoOf(details)
  var pieces = []
  var remarksOnly = false

  if (isPendingStatus(to)) {
    pieces.push([activityText('requestActivity.kind.rta.decision.reset'), 'plain'])
  } else if (isPendingStatus(from)) {
    pieces.push([activityText('requestActivity.kind.rta.decision.set'), 'lead'], [to, 'obj'])

    if (String(to).toLowerCase() === 'approved' && present(n)) {
      pieces.push([activityText('requestActivity.kind.rta.decision.onOption', { n: n }), 'tail'])
    }
  } else if (String(from).toLowerCase() === String(to).toLowerCase()) {
    // Only the date or the remarks moved: the API records { from: 'Approved', to: 'Approved', ... }.
    remarksOnly = details.remarksChanged === true
    pieces.push([activityText(remarksOnly ? 'requestActivity.kind.rta.decision.remarksOnly' : 'requestActivity.kind.rta.decision.dateOnly'), 'plain'])
  } else {
    pieces.push([activityText('requestActivity.kind.rta.decision.changed', { from: from }), 'lead'], [to, 'obj'])
  }

  if (details.remarksChanged === true && !remarksOnly) {
    pieces.push([activityText('requestActivity.kind.rta.decision.andRemarks'), 'tail'])
  }

  if (present(details.date) && !isPendingStatus(to)) {
    var date = ctx && typeof ctx.formatDate === 'function' ? ctx.formatDate(details.date) : String(details.date)

    pieces.push([activityText('requestActivity.kind.approval_decided.dated', { date: date }), 'tail'])
  }

  return sentence(pieces)
}

/**
 * The sentence of an RTA activity row, { text, parts }, or null when the kind is not an RTA's (or is
 * one a workflow shares and the request is not an RTA: request_created / completed / rejected read
 * "this RTA" only when ctx.rtx is 'RTA', so another request's rows stay as they were).
 */
function rtaWhat (k, row, details, ctx) {
  var rta = !!ctx && ctx.rtx === 'RTA'
  var towerCo = partyOrg('towerco', ctx)
  var telco = partyOrg('owner', ctx)
  var n = optionNoOf(details)
  var option = optionText(n)

  switch (k) {
    case 'request_created':
      if (!rta) return null

      // v2.1: "Created Jalan Ampang (RTA-000918) for Telco Beta". The request makes no option (decision 153), so there is no "with option 1"; the name
      // and reference come with the row (API-2, DEV-85). A row of the v2 build that still says `optionNo` reads the same.
      if (present(details.name) && details.name !== '') {
        return sentence([
          ['Created ', 'lead'], [details.name, 'obj'],
          [present(details.reference) && details.reference !== '' ? ' (' + details.reference + ')' : '', 'tail'],
          [' for ' + telco, 'tail']
        ])
      }

      if (present(details.clientId) && row.actor && row.actor.organisation && present(row.actor.organisation.id) &&
          Number(row.actor.organisation.id) !== Number(details.clientId)) {
        return sentence([[activityText('requestActivity.kind.rta.created.for'), 'lead'], [telco, 'obj']])
      }
      return null
    case 'fields_changed':
      // A request or option edit on an RTA carries `changed` (API P-S29); a row without it reads as any other field change.
      return rta && details.changed && typeof details.changed === 'object' && !Array.isArray(details.changed) ? rtaFieldsWhat(details, option) : null
    case 'option_added':
      return sentence([['Added ', 'lead'], [option, 'obj'], [details.backToDraft === true ? activityText('requestActivity.kind.rta.added.backToDraft') : '', 'tail']])
    case 'option_deleted':
      return sentence([['Deleted ', 'lead'], [option, 'obj'], [renumberedText(details.renumbered), 'tail']])
    case 'options_proposed':
      var proposed = optionsText(details.optionNos)

      return sentence([
        ['Proposed ', 'lead'], [proposed || 'the options', 'obj'],
        [details.toParty === 'towerco' ? ' to ' + towerCo : (details.toParty === 'owner' ? ' to ' + telco : ''), 'tail']
      ])
    case 'rta_back_to_draft':
      var back = optionsText(details.optionNos)

      return sentence([['Took ', 'lead'], [back || 'the options', 'obj'], [' back to Searching', 'tail']])
    case 'option_reviewed':
      // API-7: `to` is what the review was submitted as. A Tower Co user's review is "for {Telco}" (it records the client's answer, 76)
      // unless the Telco proposed to it (`proposedTo` 'towerco': it answers in its own right).
      var word = responseWord(details.to)
      var forClient = details.byParty === 'towerco' && details.proposedTo !== 'towerco' ? ' for ' + telco : ''
      var after = (details.decisionReset === true ? activityText('requestActivity.kind.rta.reviewed.cleared') : '') +
        (details.backToDraft === true ? activityText('requestActivity.kind.rta.reviewed.backToDraft') : '')

      if (word === 'Pending') {
        return sentence([['Set ', 'lead'], [option, 'obj'], [' back to pending' + forClient + after, 'tail']])
      }

      return sentence([[word + ' ', 'lead'], [option, 'obj'], [forClient + after, 'tail']])
    case 'option_declined':
      return sentence([
        ['Declined the approved ', 'lead'], [present(n) ? option : 'option', 'obj'],
        [present(details.reason) && String(details.reason) !== '' ? ': “' + cutAt500(details.reason) + '”' : '', 'tail'],
        [details.backToDraft === true ? activityText('requestActivity.kind.rta.added.backToDraft') : '', 'tail']
      ])
    case 'rta_towerco_decided':
      return rtaDecidedWhat(details, ctx)
    case 'rta_decision_reset':
      if (details.cause === 'submit' || details.cause === 'decline') {
        return sentence([[activityText('requestActivity.kind.rta.reset.' + details.cause, { option: option }), 'plain']])
      }
      return sentence([[activityText('requestActivity.kind.rta.reset.other'), 'plain']])
    case 'rta_decision_unlocked':
      return sentence([[activityText('requestActivity.kind.rta.unlocked'), 'plain']])
    case 'rta_transitioned':
      return sentence([['Submitted the decision: ', 'lead'], [option, 'obj'], [activityText('requestActivity.kind.rta.transitioned.tail'), 'tail']])
    case 'rta_completion_edited':
      var labels = completionLabels(details)

      return labels.length === 0
        ? sentence([['Edited ', 'lead'], ['Finalize', 'obj']])
        : sentence([['Edited ', 'lead'], ['Finalize', 'obj'], [': ' + sharerListText(labels), 'tail']])
    case 'request_completed':
      if (!rta) return null

      var named = present(details.siteName) && details.siteName !== ''

      // "Completed the RTA: created {Site name}", and " and an RTS opened" for a Telco client (rtsId). The API's outcome (XOL-693) says "linked"
      // or "with no RTS" instead; a row from before it has none, and reads as "opened" when it has an rtsId (the one read rule).
      var rtsTail = ''

      if (details.rtsOutcome === 'linked') {
        rtsTail = activityText('requestActivity.kind.rta.completed.rtsLinked')
      } else if (details.rtsOutcome === 'none') {
        rtsTail = activityText('requestActivity.kind.rta.completed.rtsNone')
      } else if (details.rtsOutcome === 'opened' || present(details.rtsId)) {
        rtsTail = activityText('requestActivity.kind.rta.completed.rts')
      }

      return sentence([
        [named ? activityText('requestActivity.kind.rta.completed.lead') : activityText('requestActivity.kind.rta.completed.noName'), named ? 'lead' : 'plain'],
        [named ? details.siteName : '', 'obj'],
        [rtsTail, 'tail']
      ])
    case 'request_rejected':
      if (!rta) return null

      // v2 (API-11): the reason is `remarks`, plain text of at most 500.
      return present(details.remarks) && String(details.remarks) !== ''
        ? sentence([[activityText('requestActivity.kind.rta.rejected.lead'), 'lead'], ['“' + cutAt500(details.remarks) + '”', 'tail']])
        : sentence([[activityText('requestActivity.kind.rta.rejected'), 'plain']])
    case 'rta_site_deleted':
      if (!present(details.siteName) || details.siteName === '') {
        return sentence([[activityText('requestActivity.kind.rta.siteDeleted.noName'), 'plain']])
      }
      return sentence([
        [details.siteName, 'obj'],
        [activityText('requestActivity.kind.rta.siteDeleted', { siteId: details.siteId, by: deletedBy(row, details) }), 'tail']
      ])
  }

  return null
}

// Fields the server computes after a save: never listed as something the user changed (API P-S29), even if a row carries them.
var RTA_SERVER_COMPUTED = ['distanceFromNominal', 'distanceAccessRoad', 'distanceNearestPower', 'distanceNearestBuilding', 'summary']

// Metres, for the value text of a size.
var RTA_METRE_FIELDS = ['structureHeight', 'antennaHeight', 'width', 'depth']

// The value of one change as the sentence prints it: "not set" for nothing, Yes / No for a tick, "30 m" for a size, per-type counts for `markers`
// ({ access: 1, power: 0, building: 2 } -> "1 access, 2 building"), text cut at 500. Never a raw object.
function rtaFieldValue (path, v) {
  var leaf = String(path).split('.').pop()

  if (v === undefined || v === null || v === '') return activityText('requestActivity.kind.rta.fields.notSet')
  if (v === true) return copyText('requestSync.notice.yes')
  if (v === false) return copyText('requestSync.notice.no')

  if (typeof v === 'object') {
    if (leaf !== 'markers') return copyText('requestSync.field.otherValue')

    var counts = ['access', 'power', 'building'].filter(function (t) { return Number(v[t]) > 0 }).map(function (t) {
      return activityText('requestActivity.kind.rta.fields.markers.count', { n: v[t], type: t })
    })

    return counts.length > 0 ? counts.join(', ') : activityText('requestActivity.kind.rta.fields.markers.none')
  }

  var text = cutAt500(String(v))

  return RTA_METRE_FIELDS.indexOf(leaf) !== -1 && !isNaN(Number(v)) ? text + ' m' : text
}

// The point's four paths: the request's latitude / longitude and an option's actualLat / actualLng. They read as ONE "Lat / Lng" (the pair is saved
// together, P-S27), and a row that holds nothing else is a move: "Moved the requested point (Lat / Lng)", "Moved option 2's pin (Lat / Lng)".
var RTA_POINT_PATHS = ['state.nominal.latitude', 'state.nominal.longitude', 'actualLat', 'actualLng']

// Does this `fields_changed` row hold nothing but the point of the request ('request'), or of an option ('option')? '' for any other row.
function rtaPointMove (details) {
  var keys = details && details.changed && typeof details.changed === 'object' && !Array.isArray(details.changed) ? Object.keys(details.changed) : []

  keys = keys.filter(function (k) { return RTA_SERVER_COMPUTED.indexOf(String(k).split('.').pop()) === -1 })

  if (keys.length === 0 || !keys.every(function (k) { return RTA_POINT_PATHS.indexOf(k) !== -1 })) {
    return ''
  }

  return keys[0].indexOf('state.nominal.') === 0 ? 'request' : 'option'
}

// The sentence of a request edit or an option edit (`fields_changed` with `changed`, API P-S29; UX spec 4.8). One row may hold several fields (a burst of
// saves by one person in one edit session merges into one row, with the first `from` and the latest `to` of each). The five locality levels read as ONE
// "Locality", and the point's two paths as ONE "Lat / Lng". Up to three fields print their values after the colon, one "a → b" each, in the order of the
// list; more are only listed (the row's changes dialog has the values).
function rtaFieldsWhat (details, option) {
  var isOption = present(details.optionId) || present(details.optionNo) || details.table === 'site_candidates'
  var move = rtaPointMove(details)

  if (move === 'request') {
    return sentence([
      [activityText('requestActivity.kind.rta.fields.moved.request.lead'), 'lead'],
      [activityText('requestActivity.kind.rta.fields.moved.request.obj'), 'obj'],
      [activityText('requestActivity.kind.rta.fields.moved.request.tail'), 'tail']
    ])
  }

  if (move === 'option') {
    return sentence([
      [activityText('requestActivity.kind.rta.fields.moved.lead'), 'lead'], [option, 'obj'],
      [activityText('requestActivity.kind.rta.fields.moved.option.tail'), 'tail']
    ])
  }

  var items = []
  var byLabel = {}

  Object.keys(details.changed).forEach(function (path) {
    var leaf = String(path).split('.').pop()

    if (RTA_SERVER_COMPUTED.indexOf(leaf) !== -1) {
      return
    }

    var entry = details.changed[path] && typeof details.changed[path] === 'object' ? details.changed[path] : {}
    var label = RTA_POINT_PATHS.indexOf(path) !== -1 ? activityText('requestActivity.kind.rta.fields.point') : fieldLabel(isOption ? 'option' : 'action', path)
    var from = rtaFieldValue(path, entry.from)
    var to = rtaFieldValue(path, entry.to)
    var empty = activityText('requestActivity.kind.rta.fields.notSet')

    if (has(byLabel, label)) {
      var item = byLabel[label]

      // Locality and the point: the parts that changed, joined; a part that was empty is not listed.
      item.from = from === empty ? item.from : (item.from === empty ? from : item.from + ', ' + from)
      item.to = to === empty ? item.to : (item.to === empty ? to : item.to + ', ' + to)
      return
    }

    byLabel[label] = { label: label, from: from, to: to }
    items.push(byLabel[label])
  })

  var lead = activityText('requestActivity.kind.rta.fields.lead')
  var where = isOption ? activityText('requestActivity.kind.rta.fields.on', { option: option }) : activityText('requestActivity.kind.rta.fields.onRequest')

  if (items.length === 0) {
    return sentence([[lead, 'lead'], [isOption ? option : 'the request', 'obj']])
  }

  var tail = where

  if (items.length <= 3) {
    tail += ': ' + items.map(function (i) {
      return activityText('requestActivity.kind.rta.fields.change', { from: i.from, to: i.to })
    }).join(', ')
  }

  return sentence([[lead, 'lead'], [sharerListText(items.map(function (i) { return i.label })), 'obj'], [tail, 'tail']])
}

// "; options 4 and 5 are now 3 and 4" from `renumbered: [{ from, to }]` (D133), or '' when nothing moved.
function renumberedText (moves) {
  var list = Array.isArray(moves) ? moves.filter(function (m) { return m && present(m.from) && present(m.to) }) : []

  if (list.length === 0) {
    return ''
  }

  var from = list.map(function (m) { return m.from })
  var to = list.map(function (m) { return m.to })

  return '; ' + (list.length === 1 ? 'option ' : 'options ') + numbersText(from) + (list.length === 1 ? ' is' : ' are') + ' now ' + numbersText(to)
}

/**
 * One activity row as the feed shows it.
 *
 * @param {Object} row the API row ({ id, kind, occurred_at, step_no, party_no, actor,
 *                     file, details, has_values, mail })
 * @param {Object} ctx { rtx, formatDate, formatDateTime, orgs: { towerco, owner,
 *                     provider } (short names), labelCtx: { towerCo, telco, provider } }
 * @return {Object} { id, kind, when, dayKey: 'YYYY-MM-DD' (app clock), time: '12:51 am',
 *                  who: { system, name, org, text }, step: { text, request }, text,
 *                  parts: [{ text, tone: lead | obj | file | tail | plain, file? }],
 *                  icon: 'fa-…', tone: info | success | danger,
 *                  tag: { text, type, icon } | null, dialog: 'mail' | 'changes' | null, batchId }
 *                  `text` is the whole sentence and stays the source of the aria-label and the
 *                  changes dialog; `parts` joined are the same text.
 */
export function activityRow (row, ctx) {
  row = row || {}
  ctx = ctx || {}

  var details = row.details && typeof row.details === 'object' ? row.details : {}
  var what = { text: '', tag: null, dialog: null }
  var rtx = ctx.rtx || 'request'
  var k = row.kind
  var title = details.title
  var needles = []
  var parts = null
  var name
  // XOL-67: an RTA kind (or a shared kind on an RTA) has its own sentence; null leaves the switch alone.
  var rtaSaid = k === 'mail' ? null : rtaWhat(k, row, details, ctx)

  if (rtaSaid) {
    what.text = rtaSaid.text
    parts = rtaSaid.parts
  }

  switch (rtaSaid ? null : k) {
    case 'mail':
      what = mailWhat(row, ctx)
      parts = what.parts
      break
    case 'request_created':
      what.text = activityText('requestActivity.kind.request_created', { rtx: rtx })
      break
    case 'fields_changed':
      what.text = fieldsText(row, details, ctx)
      // "Changed " then the field list as the object.
      if (what.text.indexOf('Changed ') === 0) {
        parts = [part('Changed ', 'lead'), part(what.text.slice('Changed '.length), 'obj')]
      }
      break
    case 'recipients_changed':
      what.text = recipientsText(details, ctx)
      break
    case 'mail_turned_on':
    case 'mail_turned_off':
      what.text = activityText('requestActivity.kind.' + k, { rtx: rtx })
      break
    case 'approval_decided':
      what.text = approvalDecidedText(details, ctx)
      parts = approvalParts(what.text, details)
      break
    case 'approval_locked':
      what.text = activityText('requestActivity.kind.approval_locked', { status: details.status || null })
      break
    case 'approval_unlocked':
    case 'step_completed':
    case 'step_reopened':
      what.text = activityText('requestActivity.kind.' + k)
      break
    case 'request_completed':
    case 'request_rejected':
      what.text = activityText('requestActivity.kind.' + k, { rtx: rtx, status: details.status || null })
      break
    case 'category_added':
    case 'category_removed':
      title = title || '(untitled)'
      what.text = activityText('requestActivity.kind.' + k, { title: title })
      needles = [{ str: quoted(title), tone: 'obj' }]
      break
    case 'file_uploaded':
    case 'file_replaced':
      name = fileName(row, details)
      what.text = title
        ? activityText('requestActivity.kind.' + k, { name: name, title: title })
        : activityText('requestActivity.kind.' + k + '.noTitle', { name: name })
      needles = [fileNeedle(name)]
      break
    case 'file_removed':
    case 'file_approved':
    case 'file_unapproved':
    case 'file_details_changed':
    case 'file_first_download':
    case 'file_downloaded':
      name = fileName(row, details)
      // XOL-650: only the first-download copy has a via-e-mail variant (file_downloaded is
      // always via e-mail); anything but the exact 'email' flag keeps the plain copy.
      what.text = activityText(
        'requestActivity.kind.' + k + (k === 'file_first_download' && details.via === 'email' ? '.viaEmail' : ''),
        { name: name }
      )
      needles = [fileNeedle(name)]
      break
    case 'file_renamed':
      var from = details.from || '(unnamed file)'
      var to = details.to || '(unnamed file)'

      what.text = activityText('requestActivity.kind.file_renamed', { from: from, to: to })
      needles = [fileNeedle(from), fileNeedle(to)]
      break
    case SHARER_KIND.created:
    case SHARER_KIND.updated:
    case SHARER_KIND.shared:
    case SHARER_KIND.unshared:
    case SHARER_KIND.linked:
    case SHARER_KIND.finance:
      var sharer = sharerWhat(k, details, rtx)

      what.text = sharer.text
      parts = sharer.parts
      break
    default:
      // Never the raw kind. (An RTA kind arrives here with its sentence already made.)
      if (!rtaSaid) {
        what.text = activityText('requestActivity.kind.other')
      }
  }

  if (!parts) {
    parts = needles.length > 0 ? splitParts(what.text, needles) : [part(what.text, 'plain')]
  }

  // A row that has stored values (a field edit, a recipient edit) opens the change dialog.
  if (k !== 'mail' && !isSharerKind(k) && row.has_values === true) {
    what.dialog = 'changes'
  }

  var look = activityKindLook(k, details)
  var at = row.occurred_at ? toAppTime(row.occurred_at) : null

  var out = {
    id: row.id,
    kind: k,
    when: row.occurred_at ? (typeof ctx.formatDateTime === 'function' ? ctx.formatDateTime(row.occurred_at) : String(row.occurred_at)) : '',
    dayKey: at ? at.format('YYYY-MM-DD') : '',
    time: at ? at.format('hh:mm a') : '',
    who: whoOf(row),
    step: stepOf(row, ctx),
    text: what.text,
    parts: parts,
    icon: look.icon,
    tone: look.tone,
    tag: what.tag || null,
    dialog: what.dialog || null,
    batchId: k === 'mail' && row.mail && present(row.mail.batch_id) ? row.mail.batch_id : null
  }

  // XOL-67 (P-S10 as corrected): a proposal's mail row carries `option_count` in its ACTIVITY details (its `option_no` is null; the batch read
  // `GET /mail/{batch}` does not hold it). Exposed as `optionCount` for the rows that have one; no sentence uses it yet (the UX spec words none).
  if (k === 'mail' && ctx.rtx === 'RTA' && details.event === 'options_proposed' && present(details.option_count) && Number(details.option_count) > 0) {
    out.optionCount = Number(details.option_count)
  }

  return out
}

/* ------------------------------------------------------------------------
   The field-change dialog: GET /actions/{id}/activity/{activity} -> `changes`
------------------------------------------------------------------------ */

// JSON with sorted keys, so two equal values compare equal however the API ordered them.
function stableJson (v) {
  if (v === undefined) {
    return 'null'
  }
  if (Array.isArray(v)) {
    return '[' + v.map(stableJson).join(',') + ']'
  }
  if (v && typeof v === 'object') {
    return '{' + Object.keys(v).sort().map(function (k) {
      return JSON.stringify(k) + ':' + stableJson(v[k])
    }).join(',') + '}'
  }
  return JSON.stringify(v)
}

function normalised (v) {
  return present(v) ? v : null
}

function sameValue (a, b) {
  return stableJson(normalised(a)) === stableJson(normalised(b))
}

function truthy (v) {
  return v === true || v === 1 || v === '1' || v === 'true'
}

/** The title of the dialog: field changes, or recipient changes. */
export function changesTitle (row) {
  return activityText(row && row.kind === 'recipients_changed' ? 'requestActivity.changes.title.recipients' : 'requestActivity.changes.title.fields')
}

// "Upload", "Approval", "Upload and Approval", or '' when neither is ticked.
function ticksText (e) {
  var u = truthy(e && e.upload)
  var a = truthy(e && e.approval)

  if (u && a) return activityText('requestActivity.changes.ticks.both')
  if (u) return activityText('requestActivity.changes.ticks.upload')
  if (a) return activityText('requestActivity.changes.ticks.approval')

  return ''
}

// Who a recipient entry is, from the snapshot taken when the edit was recorded.
function entryWho (e) {
  e = e && typeof e === 'object' ? e : {}

  if (present(e.contact_id)) {
    if (present(e.name) && present(e.email)) {
      return activityText('requestActivity.changes.entry.contact', { name: e.name, email: e.email })
    }
    return e.name || e.email || activityText('requestActivity.changes.entry.unknown')
  }

  if (present(e.email)) {
    return activityText('requestActivity.changes.entry.typed', { email: e.email })
  }

  return e.name || activityText('requestActivity.changes.entry.unknown')
}

function entryKey (e) {
  return recipientKey(e) || 'j:' + stableJson(e)
}

function indexByKey (list, keyOf) {
  var map = {}
  var order = []

  ;(Array.isArray(list) ? list : []).forEach(function (e) {
    var k = keyOf(e)

    if (!Object.prototype.hasOwnProperty.call(map, k)) {
      order.push(k)
    }
    map[k] = e
  })

  return { map: map, order: order }
}

// added / removed / kept-with-a-difference entries of `to` against `from`.
function keyedDiff (from, to, keyOf, differs) {
  var a = indexByKey(from, keyOf)
  var b = indexByKey(to, keyOf)
  var out = { added: [], removed: [], changed: [] }

  b.order.forEach(function (k) {
    if (!Object.prototype.hasOwnProperty.call(a.map, k)) {
      out.added.push(b.map[k])
    } else if (differs && differs(a.map[k], b.map[k])) {
      out.changed.push({ before: a.map[k], after: b.map[k] })
    }
  })

  a.order.forEach(function (k) {
    if (!Object.prototype.hasOwnProperty.call(b.map, k)) {
      out.removed.push(a.map[k])
    }
  })

  return out
}

var RECIPIENT_PATH = /^emails\.(towerco|owner|provider)\.(additional|unsubscribed)$/

function ticksDiffer (a, b) {
  return truthy(a && a.upload) !== truthy(b && b.upload) || truthy(a && a.approval) !== truthy(b && b.approval)
}

function entryLine (e) {
  var ticks = ticksText(e)

  return ticks ? activityText('requestActivity.changes.entry.withTicks', { who: entryWho(e), ticks: ticks }) : entryWho(e)
}

function recipientGroups (list, from, to) {
  var d = keyedDiff(from, to, entryKey, ticksDiffer)
  var addedKey = list === 'unsubscribed' ? 'unsubscribed' : 'added'
  var removedKey = list === 'unsubscribed' ? 'resubscribed' : 'removed'

  return [
    { key: addedKey, label: activityText('requestActivity.changes.group.' + addedKey), items: d.added.map(entryLine) },
    { key: removedKey, label: activityText('requestActivity.changes.group.' + removedKey), items: d.removed.map(entryLine) },
    {
      key: 'ticks',
      label: activityText('requestActivity.changes.group.ticks'),
      items: d.changed.map(function (c) {
        return activityText('requestActivity.changes.entry.ticks', {
          who: entryWho(c.after),
          before: ticksText(c.before) || activityText('requestActivity.changes.empty'),
          after: ticksText(c.after) || activityText('requestActivity.changes.empty')
        })
      })
    }
  ].filter(function (g) { return g.items.length > 0 })
}

function plainGroups (from, to) {
  var d = keyedDiff(from, to, stableJson, null)

  return [
    { key: 'added', label: activityText('requestActivity.changes.group.added'), items: d.added.map(describeEntry) },
    { key: 'removed', label: activityText('requestActivity.changes.group.removed'), items: d.removed.map(describeEntry) }
  ].filter(function (g) { return g.items.length > 0 })
}

// A raw date or date-time string that describeScalar left alone, as the user reads it.
function scalarText (path, value, ctx) {
  var fmt = { date: ctx.formatDate, datetime: ctx.formatDateTime }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { text: copyText('requestSync.field.otherValue'), empty: false }
  }

  var text = describeScalar(path, value, fmt)
  var empty = text === activityText('requestActivity.changes.empty')

  if (typeof text === 'string') {
    var m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}:\d{2}))?/.exec(text)

    if (m && text.length <= 19 && typeof ctx.formatDate === 'function' && typeof ctx.formatDateTime === 'function') {
      text = (!m[2] || m[2] === '00:00:00') ? ctx.formatDate(m[1]) : ctx.formatDateTime(text)
    }
  }

  return { text: text, empty: empty }
}

/**
 * The blocks of the field-change dialog, one per entry of `changes`, in the API's order.
 *
 * @param {Array}  changes [{ table, row_id, path, from, to }]
 * @param {Object} row     the activity row (for the step a remarks field belongs to)
 * @param {Object} ctx     { formatDate, formatDateTime, labelCtx: { towerCo, telco, provider } }
 * @return {Array} [{ key, path, label, type: 'scalar' | 'text' | 'list', changedBack,
 *                 before: { text, empty } (scalar), after, beforeHtml / afterHtml
 *                 (text), groups: [{ key, label, items }] and noNet (list) }]
 */
export function changeBlocks (changes, row, ctx) {
  row = row || {}
  ctx = ctx || {}

  return (Array.isArray(changes) ? changes : []).map(function (c, i) {
    var path = String(c && c.path !== undefined ? c.path : '')
    var from = c ? c.from : null
    var to = c ? c.to : null
    var type = valueKind(path, present(to) ? to : from)
    var labelCtx = extend({}, ctx.labelCtx, { partyNo: row.party_no, stepNo: row.step_no })
    var block = {
      key: i,
      path: path,
      label: fieldLabel(c && c.table === 'action_steps' ? 'step' : 'action', path, labelCtx),
      type: type,
      changedBack: sameValue(from, to)
    }

    if (type === 'text') {
      block.beforeHtml = present(from) ? String(from) : null
      block.afterHtml = present(to) ? String(to) : null
    } else if (type === 'list') {
      var m = RECIPIENT_PATH.exec(path)

      block.groups = m ? recipientGroups(m[2], from, to) : plainGroups(from, to)
      block.noNet = block.groups.length === 0
    } else {
      block.before = scalarText(path, from, ctx)
      block.after = scalarText(path, to, ctx)
    }

    return block
  })
}
